import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import localforage from 'localforage';
import { format, parseISO, addDays, subDays } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import CampManagementForm from './screens/CampManagementForm';
import CampOverview from './screens/CampOverview';
import CampList from './screens/CampList';
import AlertManagementScreen from './screens/AlertManagementscreen';
import DashboardHome from './screens/DashboardHome';
import UserModal from './components/UserModal';
import EnhancedRoomAssignment from './screens/EnhancedRoomAssignment';
import ProfileEditModal from "./components/ProfileEditModal";
import axios from 'axios';
import UserHistoryView from './components/UserHistoryView';
import GroupManagement from './screens/GroupManagement';
import FileManagement from './screens/FileManagement';

import { 
  Search, Calendar, Clock, RefreshCw, User, Mail, LogOut, Menu, 
  Settings, Bell, Tent, Layout, PlusCircle, Home, Camera, ChevronDown, 
  ChevronUp, X, Activity, Shield, Users, FileText, HelpCircle, Database,
  BarChart2, Heart, Eye, Calendar as CalendarIcon, Zap, Briefcase,
  Code, GitBranch, Send, ChevronRight, Check, Loader, Sun, Moon,
  AlignStartVertical
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Create animation keyframes
const keyframes = {
  fadeIn: `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `,
  slideInRight: `
    @keyframes slideInRight {
      from { transform: translateX(30px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `,
  slideInLeft: `
    @keyframes slideInLeft {
      from { transform: translateX(-30px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `,
  slideInUp: `
    @keyframes slideInUp {
      from { transform: translateY(30px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `,
  slideInDown: `
    @keyframes slideInDown {
      from { transform: translateY(-30px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `,
  scaleUp: `
    @keyframes scaleUp {
      from { transform: scale(0.95); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
  `,
  pulse: `
    @keyframes pulse {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(230, 55, 110, 0.5); }
      70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(230, 55, 110, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(230, 55, 110, 0); }
    }
  `,
  shimmer: `
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
  `,
  spin: `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `,
  progress: `
    @keyframes progress {
      from { width: 0%; }
      to { width: 100%; }
    }
  `,
  floatingGlow: `
    @keyframes floatingGlow {
      0% { box-shadow: 0 5px 15px rgba(230, 55, 110, 0.3); }
      50% { box-shadow: 0 5px 25px rgba(230, 55, 110, 0.5); }
      100% { box-shadow: 0 5px 15px rgba(230, 55, 110, 0.3); }
    }
  `,
  shine: `
    @keyframes shine {
      0% { background-position: -100px; }
      60% { background-position: 140px; }
      100% { background-position: 140px; }
    }
  `,
  float: `
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
  `,
  ripple: `
    @keyframes ripple {
      0% { box-shadow: 0 0 0 0 rgba(230, 55, 110, 0.3); }
      100% { box-shadow: 0 0 0 20px rgba(230, 55, 110, 0); }
    }
  `,
  backgroundMove: `
    @keyframes backgroundMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `,
  bottomBarIndicator: `
    @keyframes bottomBarIndicator {
      0% { transform: scale(0); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }
  `
};

// Add keyframes to document
const addKeyframesToDocument = () => {
  const styleEl = document.createElement('style');
  styleEl.textContent = Object.values(keyframes).join('\n');
  document.head.appendChild(styleEl);
  
  return () => {
    document.head.removeChild(styleEl);
  };
};

// Custom hook for intersection observer animations
const useIntersectionObserver = (options = {}) => {
  const [elements, setElements] = useState([]);
  const [entries, setEntries] = useState([]);

  const observer = useRef(null);

  const optionsRef = useRef(options);

  useEffect(() => {
    const { root = null, rootMargin = '0px', threshold = 0 } = optionsRef.current;
    
    observer.current = new IntersectionObserver(
      (observedEntries) => {
        setEntries(observedEntries);
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    return () => observer.current?.disconnect();
  }, []);

  useEffect(() => {
    const { current: currentObserver } = observer;
    
    if (currentObserver && elements.length) {
      elements.forEach(element => currentObserver.observe(element));
    }
    
    return () => {
      if (currentObserver && elements.length) {
        elements.forEach(element => currentObserver.unobserve(element));
      }
    };
  }, [elements]);

  const ref = useCallback((element) => {
    if (element && !elements.includes(element)) {
      setElements(prevElements => [...prevElements, element]);
    }
  }, []);

  return [ref, entries];
};

// Blurred svg background for sections
const BlurredBackgroundSVG = () => (
  <svg
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) scale(1.5)',
      width: '100%',
      height: '100%',
      zIndex: -1,
      opacity: 0.3,
      filter: 'blur(40px)',
    }}
    width="800"
    height="800"
    viewBox="0 0 800 800"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <filter id="a" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="50" />
      </filter>
    </defs>
    <g filter="url(#a)">
      <circle cx="400" cy="300" r="100" fill="#e6376e" />
      <circle cx="500" cy="400" r="120" fill="#3b82f6" />
      <circle cx="300" cy="500" r="80" fill="#10b981" />
    </g>
  </svg>
);

// Custom hooks
const useTheme = () => {
  const [theme, setTheme] = useState('light');
  
  useEffect(() => {
    const storedTheme = localStorage.getItem('theme') || 'light';
    setTheme(storedTheme);
    document.documentElement.setAttribute('data-theme', storedTheme);
  }, []);
  
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };
  
  return [theme, toggleTheme];
};

// Main component
const AdminDashboard = () => {
  // Theme
  const [theme, toggleTheme] = useTheme();
  
  // Animation ref
  const [animRef, animEntries] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px 0px -100px 0px'
  });
  
  // Add animation keyframes once on mount
  useEffect(() => {
    const cleanup = addKeyframesToDocument();
    return cleanup;
  }, []);
  
  // All original state variables
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [username, setUsername] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [userHistory, setUserHistory] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const [dateFilter, setDateFilter] = useState(new Date());
  const [viewMode, setViewMode] = useState('current');
  const [profileImage, setProfileImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showCampOverview, setShowCampOverview] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const contentRefs = useRef({});
  const [bulkUserInput, setBulkUserInput] = useState('');
  const [missingUsers, setMissingUsers] = useState([]);
  const [showCampForm, setShowCampForm] = useState(false);
  const [existingCampData, setExistingCampData] = useState(null);
  const [showCampList, setShowCampList] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });
  
  // New enhanced UI state variables
  const [pageLoaded, setPageLoaded] = useState(false);
  const [showLoginAnimation, setShowLoginAnimation] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [loginAnimation, setLoginAnimation] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // grid or list view
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [accentColor, setAccentColor] = useState('#da291c');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [pageTransition, setPageTransition] = useState(false);
  const [token, setToken] = useState(null);

  const [contentAnimation, setContentAnimation] = useState(true);
  const [quickActions, setQuickActions] = useState([
    { id: 1, name: 'New User', icon: <User size={16} />, action: () => console.log('New User') },
    { id: 2, name: 'New Camp', icon: <Tent size={16} />, action: () => setShowCampForm(true) },
    { id: 3, name: 'Alerts', icon: <Bell size={16} />, action: () => setShowAlertModal(true) },
  ]);
  const [weatherInfo, setWeatherInfo] = useState({ temp: '24°C', condition: 'Sunny' });
  const [chartData, setChartData] = useState([
    { month: 'Jan', active: 45, inactive: 12 },
    { month: 'Feb', active: 52, inactive: 18 },
    { month: 'Mar', active: 48, inactive: 14 },
    { month: 'Apr', active: 61, inactive: 9 },
    { month: 'May', active: 55, inactive: 15 },
    { month: 'Jun', active: 67, inactive: 6 },
  ]);
  
  // Base URL for API calls
  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

  // Mock data for recent activities
  const recentActivities = [
    { id: 1, type: 'status', user: 'Anna Müller', action: 'changed status to', target: 'Absent', time: '10 minutes ago', avatar: '/api/placeholder/40/40' },
    { id: 2, type: 'camp', user: 'Thomas Weber', action: 'created a new camp', target: 'Summer Camp 2025', time: '2 hours ago', avatar: '/api/placeholder/40/40' },
    { id: 3, type: 'user', user: 'Lisa Schmidt', action: 'updated profile', target: '', time: '5 hours ago', avatar: '/api/placeholder/40/40' },
    { id: 4, type: 'room', user: 'Max Schneider', action: 'assigned room', target: 'Room 104', time: 'Yesterday', avatar: '/api/placeholder/40/40' },
  ];

  // Generate dashboard stats
  const dashboardStats = useMemo(() => [
    { id: 1, title: 'Total Users', value: users.length, icon: <Users size={24} />, color: '#3B82F6', growth: '+12%' },
    { id: 2, title: 'Active Users', value: filteredUsers.filter(u => u.status !== 'CHECKEDOUT').length, icon: <Activity size={24} />, color: '#10B981', growth: '+8%' },
    { id: 3, title: 'Absent Users', value: filteredUsers.filter(u => u.status === 'CHECKEDOUT').length, icon: <Clock size={24} />, color: '#F97316', growth: '-5%' },
    { id: 4, title: 'Camps', value: '5', icon: <Tent size={24} />, color: '#8B5CF6', growth: '+20%' },
  ], [users.length, filteredUsers]);

  // Simulate page loaded effect
  useEffect(() => {
    setTimeout(() => {
      setPageLoaded(true);
      // Show welcome modal only on first login
      if (isLoggedIn && !localStorage.getItem('welcomeShown')) {
        setTimeout(() => {
          setShowWelcomeModal(true);
          localStorage.setItem('welcomeShown', 'true');
        }, 1500);
      }
    }, 400);
  }, [isLoggedIn]);

  // Check screen size for responsive design
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);
  useEffect(() => {
    // Token aus localForage oder anderem Speicher abrufen
    const fetchToken = async () => {
      const token = await localforage.getItem('token');
      setToken(token);
    };
    
    fetchToken();
  }, []);
  
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Get appropriate styles based on device and theme
  const styles = useMemo(() => {
    return isMobile 
      ? { ...mobileStyles, ...(theme === 'dark' ? darkMobileStyles : {}) }
      : { ...desktopStyles, ...(theme === 'dark' ? darkDesktopStyles : {}) };
  }, [isMobile, theme]);

  // Update accent color in CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--accent-color', accentColor);
    document.documentElement.style.setProperty('--accent-color-light', `${accentColor}33`); // 20% opacity
    document.documentElement.style.setProperty('--accent-color-hover', adjustColor(accentColor, -20)); // Darker for hover
  }, [accentColor]);

  // Adjust color brightness (helper function)
  const adjustColor = (color, amount) => {
    // Convert hex to RGB
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);

    // Adjust
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));

    // Convert back to hex
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  // Change section with animation
  const changeSection = (section) => {
    setPageTransition(true);
    setTimeout(() => {
      setActiveSection(section);
      setPageTransition(false);
      
      // Trigger content animation after section change
      setContentAnimation(false);
      setTimeout(() => {
        setContentAnimation(true);
      }, 50);
      
    }, 300); // Match the CSS transition duration
  };

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${BASE_URL}/api/auth/get-all-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error loading users');

      const data = await response.json();
      const usersWithImages = data.users.map(user => ({
        ...user,
        profileImagePath: user.profileImagePath
          ? `${BASE_URL}${user.profileImagePath}`
          : '/api/placeholder/40/40'
      }));

      // Update users state
      setUsers(prevUsers => {
        // Keep sorting and filtering
        const updatedUsers = usersWithImages.map(newUser => {
          const existingUser = prevUsers.find(u => u.email === newUser.email);
          return {
            ...newUser,
            // Keep additional UI states if any
            isExpanded: existingUser?.isExpanded || false
          };
        });
        return updatedUsers;
      });

      // Update filtered users with current filters
      setFilteredUsers(prevFiltered => {
        const currentSearchText = searchText.toLowerCase();
        const filtered = usersWithImages.filter(user => {
          const matchesSearch = !currentSearchText || 
            user.username?.toLowerCase().includes(currentSearchText) ||
            user.email?.toLowerCase().includes(currentSearchText);
          const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
          return matchesSearch && matchesStatus;
        });

        // Keep current sorting
        if (sortConfig.key) {
          return [...filtered].sort((a, b) => {
            if (a[sortConfig.key] === null) return 1;
            if (b[sortConfig.key] === null) return -1;

            let comparison = 0;
            if (sortConfig.key === 'status') {
              const statusOrder = {
                'Nicht-Abgemeldet': 1,
                'CHECKEDOUT': 2,
                "SICK": 3
              };
              comparison = statusOrder[a[sortConfig.key]] - statusOrder[b[sortConfig.key]];
            } else {
              comparison = String(a[sortConfig.key]).toLowerCase()
                .localeCompare(String(b[sortConfig.key]).toLowerCase());
            }
            
            return sortConfig.direction === 'asc' ? comparison : -comparison;
          });
        }
        return filtered;
      });
    } catch (err) {
      handleError(err);
    }
  }, [searchText, statusFilter, sortConfig.key, sortConfig.direction]);

  // Handle user search with debounce
  const handleSearch = useCallback(() => {
    if (!users.length) return;
    
    const filtered = users.filter(user => {
      const matchesSearch = !searchText || 
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] === null) return 1;
        if (b[sortConfig.key] === null) return -1;

        let comparison = 0;
        if (sortConfig.key === 'status') {
          const statusOrder = {
            'Nicht-Abgemeldet': 1,
            'CHECKEDOUT': 2,
            "SICK": 3
          };
          comparison = statusOrder[a[sortConfig.key]] - statusOrder[b[sortConfig.key]];
        } else {
          comparison = String(a[sortConfig.key]).toLowerCase()
            .localeCompare(String(b[sortConfig.key]).toLowerCase());
        }
        
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredUsers(filtered);
  }, [users, searchText, statusFilter, sortConfig]);

  // Check login status on component mount
  useEffect(() => {
    checkLoginStatus();
  }, []);

  // Fetch all history when showAllHistory changes
  useEffect(() => {
    if (showAllHistory) {
      fetchAllHistory();
    }
  }, [showAllHistory]);

  // Reset user status at midnight
  useEffect(() => {
    if (isLoggedIn) {
      const scheduleStatusReset = async () => {
        const now = new Date();
        const resetTime = new Date();
        resetTime.setHours(0, 30, 0, 0); // 00:30
        
        // If current time is after 00:30, schedule for next day
        if (now > resetTime) {
          resetTime.setDate(resetTime.getDate() + 1);
        }
        
        const timeUntilReset = resetTime.getTime() - now.getTime();
        
        console.log(`Status reset scheduled for ${resetTime.toLocaleString()} (in ${Math.floor(timeUntilReset/3600000)} hours and ${Math.floor((timeUntilReset % 3600000) / 60000)} minutes)`);
        
        return setTimeout(async () => {
          try {
            console.log('Performing daily status reset...');
            const token = await localforage.getItem('token');
            if (!token) throw new Error('No token found');
            
            // API call to get all users
            const usersResponse = await fetch(`${BASE_URL}/api/auth/get-all-users`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!usersResponse.ok) {
              throw new Error('Error loading users for reset');
            }
            
            const usersData = await usersResponse.json();
            const users = usersData.users || [];
            let updatedCount = 0;
            let errorCount = 0;
            
            // Set each user to "Nicht-Abgemeldet"
            for (const user of users) {
              try {
                const statusResponse = await fetch(`${BASE_URL}/api/auth/update-status`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    email: user.email,
                    status: 'Nicht-Abgemeldet'
                  })
                });
                
                if (statusResponse.ok) {
                  updatedCount++;
                } else {
                  console.error(`Error resetting status for ${user.email}`);
                  errorCount++;
                }
              } catch (err) {
                console.error(`Error updating user ${user.email}:`, err);
                errorCount++;
              }
            }
            
            console.log(`Status reset completed: ${updatedCount} users updated, ${errorCount} errors`);
            
            // Update UI
            fetchUsers();
            if (showAllHistory) fetchAllHistory();
            
            // Schedule next reset
            const nextResetTimer = scheduleStatusReset();
            return nextResetTimer;
          } catch (err) {
            console.error('Error during status reset:', err);
            // Schedule next attempt even if there are errors
            const nextResetTimer = scheduleStatusReset();
            return nextResetTimer;
          }
        }, timeUntilReset);
      };
      
      // Start the first scheduler
      const resetTimer = scheduleStatusReset();
      
      // Cleanup on component unmount
      return () => clearTimeout(resetTimer);
    }
  }, [isLoggedIn, fetchUsers, showAllHistory]);

  // Fetch profile image
  const fetchProfileImage = useCallback(async () => {
    try {
      const token = await localforage.getItem('token');
      const userData = await localforage.getItem('user');
      
      if (!token || !userData) return;
      
      const user = JSON.parse(userData);
      if (user.profileImagePath) {
        // Add timestamp to URL to prevent caching
        const timestamp = new Date().getTime();
        const imageUrl = `${BASE_URL}${user.profileImagePath}?t=${timestamp}`;
        setProfileImage(imageUrl);
      } else {
        setProfileImage('/api/placeholder/80/80');
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
      setProfileImage('/api/placeholder/80/80');
    }
  }, []);

  // Refresh profile image periodically
  useEffect(() => {
    if (isLoggedIn) {
      fetchProfileImage(); // Initial fetch
      
      // Refresh profile image every 10 seconds
      const profileInterval = setInterval(fetchProfileImage, 10000);
      
      return () => clearInterval(profileInterval);
    }
  }, [isLoggedIn, fetchProfileImage]);

  // Check login status
  const checkLoginStatus = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      setIsLoggedIn(false);
      return;
    }

    try {
      const isTokenExpired = (token) => {
        try {
          const decodedToken = JSON.parse(atob(token.split('.')[1]));
          return Date.now() > decodedToken.exp * 1000;
        } catch (e) {
          console.error('Could not decode token:', e);
          return true;
        }
      };

      if (isTokenExpired(token)) {
        throw new Error('Token expired.');
      }

      setIsLoggedIn(true);
      fetchUsers();
    } catch (err) {
      console.error('Login status check failed:', err);
      handleLogout();
    } finally {
      setIsLoading(false);
    }
  }, [fetchUsers]);

  // Check login status on mount
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // Handle logout with animation
  const handleLogout = async () => {
    // Show subtle animation before logout
    document.body.style.transition = 'opacity 0.5s ease';
    document.body.style.opacity = '0.8';
    
    setTimeout(async () => {
      await localforage.removeItem('token');
      setIsLoggedIn(false);
      setUsers([]);
      setFilteredUsers([]);
      setShowMobileMenu(false);
      
      // Reset opacity after logout
      setTimeout(() => {
        document.body.style.opacity = '1';
      }, 300);
    }, 500);
  };

  // Get token from local storage
  const getToken = async () => {
    try {
      const token = await localforage.getItem('token');
      return token;
    } catch (error) {
      console.error('Error retrieving token:', error);
      return null;
    }
  };

  // Trigger search when needed
  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  // Poll for user updates
  useEffect(() => {
    if (isLoggedIn) {
      // Reduced polling frequency from 500ms to 5000ms (5 seconds)
      const interval = setInterval(async () => {
        try {
          await fetchUsers();
        } catch (err) {
          // Avoid stack trace for JSON parsing errors
          if (!err.message.includes('JSON')) {
            console.error('Error fetching users:', err);
          }
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, fetchUsers]);

  // Handle admin password change
  const handleAdminPasswordChange = async (userEmail, newPassword) => {
    try {
      const userToken = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          newPassword: newPassword
        }),
      });
  
      if (response.ok) {
        // Show success toast instead of alert
        setSuccessToastMessage('Password changed successfully');
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
        
        setPasswordModalVisible(false);
        setSelectedUserForPassword(null);
        setNewPassword('');
        setPasswordError('');
      } else {
        const data = await response.json();
        setPasswordError(data.message || 'Error changing password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('An error occurred');
    }
  };

  // Trigger search when filters change
  useEffect(() => {
    handleSearch();
  }, [searchText, statusFilter]);
  
  // Handle table sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;

      let comparison = 0;
      if (key === 'status') {
        // Special handling for status sorting
        const statusOrder = {
          'Nicht-Abgemeldet': 1,
          'CHECKEDOUT': 2,
          "SICK": 3
        };
        comparison = statusOrder[a[key]] - statusOrder[b[key]];
      } else {
        // Case-insensitive string comparison for text fields
        comparison = String(a[key]).toLowerCase().localeCompare(String(b[key]).toLowerCase());
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });

    setFilteredUsers(sortedUsers);
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate input fields
    if (!loginData.email || !loginData.password) {
      setError('Please fill in all fields');
      return;
    }
  
    setIsLoading(true);
    setError(null);
    setLoginAnimation(true);
  
    try {
      // First login attempt
      const loginResponse = await fetch(`${BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });
     
      const loginResult = await loginResponse.json();
  
      if (!loginResponse.ok) {
        setError(loginResult.message || 'Check your credentials');
        setProfileImage('/api/placeholder/80/80');
        setLoginAnimation(false);
        return;
      }
  
      // Store token temporarily
      const token = loginResult.token;
      
      // Verify admin role using profile endpoint
      try {
        const profileResponse = await fetch(`${BASE_URL}/api/auth/get-profile`, {
          headers: { 
            'Authorization': `Bearer ${token}`
          }
        });
  
        if (!profileResponse.ok) {
          throw new Error('Error loading profile');
        }
  
        const profileData = await profileResponse.json();
  
        // Check for admin role
        if (profileData.user.role !== 'ROLE_ADMIN') {
          setError('Access denied. Only administrators can login here.');
          setLoginAnimation(false);
          return;
        }
  
        // User is admin, store credentials
        await localforage.setItem('token', token);
        await localforage.setItem('user', JSON.stringify(loginResult.user));
        
        // Handle profile image
        const timestamp = new Date().getTime();
        let profileImageUrl;
        
        if (loginResult.user.profileImagePath) {
          profileImageUrl = `${BASE_URL}${loginResult.user.profileImagePath}?t=${timestamp}`;
          try {
            const imageResponse = await fetch(profileImageUrl);
            if (!imageResponse.ok) {
              profileImageUrl = '/api/placeholder/80/80';
            }
          } catch (imageError) {
            console.error('Error verifying profile image:', imageError);
            profileImageUrl = '/api/placeholder/80/80';
          }
        } else {
          profileImageUrl = '/api/placeholder/80/80';
        }
        
        // Set login animation complete
        setShowLoginAnimation(true);
        
        // Delay to show animation
        setTimeout(() => {
          // Update state with user data
          setUsername(loginResult.user.username);
          setProfileImage(profileImageUrl);
          setIsLoggedIn(true);
          setError(null);
          
          // Start fetching user data
          fetchUsers();
          
          // Reset login animation state
          setLoginAnimation(false);
          setShowLoginAnimation(false);
        }, 1200);
  
      } catch (profileError) {
        console.error('Error verifying admin status:', profileError);
        setError('Error verifying administrator rights');
        setLoginAnimation(false);
        return;
      }
  
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error: Please check your internet connection');
      setProfileImage('/api/placeholder/80/80');
      setLoginAnimation(false);
    } finally {
      if (!showLoginAnimation) {
        setIsLoading(false);
      }
    }
  };

  // Fetch all history
  const fetchAllHistory = async () => {
    try {
      const token = await localforage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/auth/status-history/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.history) {
        const formattedHistory = response.data.history.map(entry => ({
          ...entry,
          timestamp: new Date(entry.timestamp),
          timeAgo: formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })
        }));
        setAllHistory(formattedHistory);
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Fetch user history
  const fetchUserHistory = async (userEmail) => {
    try {
      const token = await localforage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/auth/status-history/${userEmail}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.history) {
        const formattedHistory = response.data.history.map(entry => ({
          ...entry,
          timestamp: new Date(entry.timestamp),
          timeAgo: formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })
        }));
        setUserHistory(formattedHistory);
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Handle user click
  const handleUserClick = (clickedUser) => {
    setSelectedUser(clickedUser);
  };

  // Update user status
  const updateUserStatus = async (email, newStatus) => {
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${BASE_URL}/api/auth/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, status: newStatus })
      });

      if (!response.ok) throw new Error('Status update failed');

      // Use toast instead of standard message
      setSuccessToastMessage('Status successfully updated');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
      
      fetchUsers();
      if (showAllHistory) fetchAllHistory();
      if (selectedUser?.email === email) fetchUserHistory(email);
    } catch (err) {
      handleError(err);
    }
  };

  // Handle bulk user check
  const handleBulkUserCheck = async () => {
    // Convert text field input to array
    const usernames = bulkUserInput.split('\n')
      .map(username => username.trim())
      .filter(username => username !== '');
  
    try {
      const token = await localforage.getItem('token');
      const response = await axios.post(`${BASE_URL}/api/auth/check-missing-users`, 
        { usernames },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
  
      // Set list of missing users
      setMissingUsers(response.data.missingUsers);
      
      // Show success message
      if (response.data.missingUsers.length === 0) {
        setSuccessToastMessage('All users exist in the system');
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      } else {
        setSuccessToastMessage(`Found ${response.data.missingUsers.length} missing users`);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      }
    } catch (error) {
      handleError(error);
    }
  };

  // View user history
  const handleViewUserHistory = (user) => {
    setSelectedUser(user);
    fetchUserHistory(user.email);
    setShowHistoryModal(true);
  };

  // Handle date change
  const handleDateChange = (date) => {
    setDateFilter(date);
    if (viewMode === 'history') {
      fetchHistoricalData(date);
    }
  };

  // Fetch historical data
  const fetchHistoricalData = async (date) => {
    try {
      const token = await localforage.getItem('token');
      const response = await fetch(
        `${BASE_URL}/api/auth/daily-history?date=${format(date, 'yyyy-MM-dd')}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Error loading history');
      
      const data = await response.json();
      setUserHistory(data.history);
    } catch (err) {
      handleError(err);
    }
  };

  // Handle errors
  const handleError = (err) => {
    const errorMessage = err.response?.data?.message || err.message;
    setError(errorMessage);
  
    // Only logout if token is actually missing or explicitly expired
    if (errorMessage.includes('Token') && !localStorage.getItem('token')) {
      setIsLoggedIn(false);
      localforage.removeItem('token');
    }
  
    setTimeout(() => setError(null), 3000);
  };
  
  // Group history by date
  const groupHistoryByDate = (history) => {
    const grouped = {};
    history.forEach((entry) => {
      const date = new Date(entry.timestamp).toLocaleDateString('de-DE'); // Format: DD.MM.YYYY
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(entry);
    });
    return Object.entries(grouped);
  };

  // Filter entries based on search query
  const filteredHistory = allHistory.filter((entry) =>
    entry.updatedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (entry.username && entry.username.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // Pick an image for profile
  const pickImage = async () => {
    try {
      const result = await window.showOpenFilePicker({
        types: [
          {
            description: 'Images',
            accept: {
              'image/*': ['.png', '.gif', '.jpeg', '.jpg']
            }
          }
        ],
        multiple: false
      });
      
      const file = await result[0].getFile();
      const reader = new FileReader();
      
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error picking image:', err);
    }
  };
  
  // Update profile data
  const updateProfileData = async () => {
    try {
      const token = await localforage.getItem('token');
      const userData = await localforage.getItem('user');
      
      if (token && userData) {
        const user = JSON.parse(userData);
        setUsername(user.username);
        setEmail(user.email);
        if (user.profileImagePath) {
          setProfileImage(`${BASE_URL}${user.profileImagePath}`);
        }
      }
    } catch (error) {
      console.error('Error updating profile data:', error);
    }
  };
  
  // Update profile data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      updateProfileData();
    }
  }, [isLoggedIn]);
  
  // Save profile changes
  const saveProfileChanges = async () => {
    try {
      // Check if passwords are filled
      if (newPassword || oldPassword || confirmNewPassword) {
        // Open password modal for password changes
        setPasswordModalVisible(true);
        
        // Validate passwords
        if (!oldPassword) {
          setPasswordError('Please enter your current password');
          return;
        }
        if (!newPassword) {
          setPasswordError('Please enter a new password');
          return;
        }
        if (!confirmNewPassword) {
          setPasswordError('Please confirm the new password');
          return;
        }
        if (newPassword !== confirmNewPassword) {
          setPasswordError('New passwords do not match');
          return;
        }
        
        // Try to change password
        await handleAdminPasswordChange(loginData.email, newPassword);
        
        // Close modal if password change is successful
        setPasswordModalVisible(false);
      }
  
      // Update profile picture and other profile data
      const userToken = await localforage.getItem('token');
      const formData = new FormData();
  
      formData.append('username', username);
      formData.append('email', email);
  
      if (profileImage && typeof profileImage === 'string' && !profileImage.startsWith('/api')) {
        const response = await fetch(profileImage);
        const blob = await response.blob();
        formData.append('profileImage', blob, 'profile.jpg');
      }
  
      // Update profile data
      const profileResponse = await fetch(`${BASE_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      });
  
      if (profileResponse.ok) {
        setProfileModalVisible(false);
        fetchProfileImage();
        
        // Show success toast
        setSuccessToastMessage('Profile updated successfully');
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
       
        // Reset password fields
        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setPasswordError('');
      } else {
        throw new Error('Error updating profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setPasswordError('An error occurred. Please try again.');
    }
  };
  
  // Load initial profile data
  const loadInitialProfileData = async () => {
    try {
      const userData = await localforage.getItem('user');
      if (userData) {
        const parsedData = JSON.parse(userData);
        setUsername(parsedData.username || '');
        setEmail(parsedData.email || '');
        if (parsedData.profileImagePath) {
          setProfileImage(`${BASE_URL}${parsedData.profileImagePath}`);
        }
      }
    } catch (error) {
      console.error('Error loading initial profile data:', error);
    }
  };
  
  // Load profile data when modal opens
  useEffect(() => {
    if (profileModalVisible) {
      loadInitialProfileData();
    }
  }, [profileModalVisible]);
  
  // Group history entries
  const groupedHistory = groupHistoryByDate(filteredHistory);

  // Toggle day expansion
  const toggleDay = (dateKey) => {
    const contentElement = contentRefs.current[dateKey];
    if (contentElement) {
      if (expandedDays[dateKey]) {
        // Collapse
        contentElement.style.maxHeight = `${contentElement.scrollHeight}px`; // Set to calculate height
        requestAnimationFrame(() => {
          contentElement.style.maxHeight = "0px";
        });
      } else {
        // Expand
        contentElement.style.maxHeight = "0px"; // Set to start animation
        requestAnimationFrame(() => {
          contentElement.style.maxHeight = `${contentElement.scrollHeight}px`;
        });
      }
    }

    setExpandedDays((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  };

  // Animate content based on intersection observer
  const getAnimationStyle = (entry) => {
    if (!entry) return {};
    
    return {
      opacity: entry.isIntersecting ? 1 : 0,
      transform: entry.isIntersecting 
        ? 'translateY(0)' 
        : 'translateY(20px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease',
    };
  };

  // Mini-Chart component for dashboard cards
  const MiniChart = ({ data, color }) => {
    const maxValue = Math.max(...data);
    const minValue = Math.min(...data);
    const range = maxValue - minValue;
    
    return (
      <div style={styles.miniChart}>
        {data.map((value, index) => (
          <div 
            key={index}
            style={{
              ...styles.miniChartBar,
              height: `${((value - minValue) / (range || 1)) * 100}%`,
              backgroundColor: color,
              opacity: 0.7 + (index / data.length) * 0.3,
            }}
          />
        ))}
      </div>
    );
  };

  // Welcome modal component
  const WelcomeModal = () => (
    <div style={styles.welcomeModalOverlay}>
      <motion.div 
        style={styles.welcomeModal}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div style={styles.welcomeModalHeader}>
          <div style={styles.welcomeModalLogo}>
            <img 
              src="https://www.sunrise.ch/eshop/HeaderIcons/sunrise_new_logo.svg" 
              alt="Sunrise" 
              style={styles.welcomeLogoImage} 
            />
            <h2 style={styles.welcomeLogoText}>Admin Dashboard</h2>
          </div>
          <button 
            style={styles.welcomeModalClose}
            onClick={() => setShowWelcomeModal(false)}
          >
            <X size={24} />
          </button>
        </div>
        <div style={styles.welcomeModalContent}>
          <h1 style={styles.welcomeModalTitle}>Welcome, {username || 'Admin'}!</h1>
          <p style={styles.welcomeModalText}>
            You're now using the new Sunrise Admin Dashboard with enhanced features and a modern design.
          </p>
          
          <div style={styles.welcomeFeatures}>
            <div style={styles.welcomeFeature}>
              <div style={styles.welcomeFeatureIcon}>
                <Users size={20} color={accentColor} />
              </div>
              <div style={styles.welcomeFeatureText}>
                <h3 style={styles.welcomeFeatureTitle}>User Management</h3>
                <p style={styles.welcomeFeatureDescription}>
                  Manage user accounts and check status updates
                </p>
              </div>
            </div>
            
            <div style={styles.welcomeFeature}>
              <div style={styles.welcomeFeatureIcon}>
                <Tent size={20} color={accentColor} />
              </div>
              <div style={styles.welcomeFeatureText}>
                <h3 style={styles.welcomeFeatureTitle}>Camp Management</h3>
                <p style={styles.welcomeFeatureDescription}>
                  Create and manage camps with easy room assignments
                </p>
              </div>
            </div>
            
            <div style={styles.welcomeFeature}>
              <div style={styles.welcomeFeatureIcon}>
                <Bell size={20} color={accentColor} />
              </div>
              <div style={styles.welcomeFeatureText}>
                <h3 style={styles.welcomeFeatureTitle}>Alerts & Notifications</h3>
                <p style={styles.welcomeFeatureDescription}>
                  Stay updated with real-time alerts and notifications
                </p>
              </div>
            </div>
          </div>
        </div>
        <div style={styles.welcomeModalFooter}>
          <motion.button 
            style={styles.welcomeModalButton}
            onClick={() => setShowWelcomeModal(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  // Lock icon component for password fields
  const Lock = ({ size, style }) => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={style}
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    );
  };

  // Create a toast notification component with animation
  const SuccessToast = ({ message }) => (
    <motion.div 
      style={styles.successToast}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.3 }}
    >
      <div style={styles.successToastContent}>
        <div style={styles.successToastIcon}>
          <Check size={18} />
        </div>
        <span>{message}</span>
      </div>
      <div style={styles.successToastProgress} />
    </motion.div>
  );

  // Create a notifications dropdown component
  const NotificationsDropdown = () => (
    <motion.div 
      style={styles.notificationsDropdown}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
    >
      <div style={styles.notificationsHeader}>
        <h3 style={styles.notificationsTitle}>Notifications</h3>
        <button style={styles.clearNotificationsButton}>Clear All</button>
      </div>
      <div style={styles.notificationsList}>
        <div style={styles.notificationItem}>
          <div style={{
            ...styles.notificationIcon,
            backgroundColor: '#3B82F6',
          }}>
            <Bell size={16} />
          </div>
          <div style={styles.notificationContent}>
            <div style={styles.notificationTitle}>System Update</div>
            <div style={styles.notificationMessage}>The system will undergo maintenance tonight at 23:00.</div>
            <div style={styles.notificationTime}>2 hours ago</div>
          </div>
        </div>
        <div style={styles.notificationItem}>
          <div style={{
            ...styles.notificationIcon,
            backgroundColor: '#10B981',
          }}>
            <User size={16} />
          </div>
          <div style={styles.notificationContent}>
            <div style={styles.notificationTitle}>New User Registered</div>
            <div style={styles.notificationMessage}>A new user has registered: Max Mustermann</div>
            <div style={styles.notificationTime}>5 hours ago</div>
          </div>
        </div>
        <div style={styles.notificationItem}>
          <div style={{
            ...styles.notificationIcon,
            backgroundColor: '#F97316',
          }}>
            <Activity size={16} />
          </div>
          <div style={styles.notificationContent}>
            <div style={styles.notificationTitle}>Status Change Alert</div>
            <div style={styles.notificationMessage}>5 users have been marked as absent</div>
            <div style={styles.notificationTime}>Yesterday</div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  // Create a mobile menu component with animations
  const MobileMenu = () => (
    <div style={styles.mobileMenuOverlay} onClick={() => setShowMobileMenu(false)}>
      <motion.div 
        style={styles.mobileMenu}
        // initial={{ y: '100%' }}
        // animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.4, type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()} // Verhindert, dass das Menü sich schließt, wenn darauf geklickt wird
      >
        <div style={styles.mobileMenuHandle}>
          <div style={styles.mobileMenuHandleBar}></div>
        </div>
        
        <div style={styles.mobileMenuHeader}>
          <div style={styles.mobileMenuProfile}>
            <img 
              src={profileImage || '/api/placeholder/80/80'} 
              alt="Profile" 
              style={styles.mobileMenuProfileImage}
            />
            <div style={styles.mobileMenuProfileInfo}>
              <h3 style={styles.mobileMenuProfileName}>{username || 'Admin'}</h3>
              <span style={styles.mobileMenuProfileRole}>Administrator</span>
            </div>
          </div>
          <motion.button 
            style={styles.mobileMenuCloseButton}
            onClick={() => setShowMobileMenu(false)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={24} />
          </motion.button>
        </div>
        
        <div style={styles.mobileMenuContent}>
          <div style={styles.mobileMenuNavigation}>
            <motion.button
              onClick={() => {
                setActiveSection('home');
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'home' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'home' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Home size={20} />
              <span style={styles.mobileMenuNavLabel}>Home</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('users');
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'users' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'users' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Users size={20} />
              <span style={styles.mobileMenuNavLabel}>Users</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('history');
                setShowAllHistory(true);
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'history' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'history' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Clock size={20} />
              <span style={styles.mobileMenuNavLabel}>History</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('alerts');
                setShowAlertModal(true);
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'alerts' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'alerts' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Bell size={20} />
              <span style={styles.mobileMenuNavLabel}>Alerts</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('new-camp');
                setShowCampForm(true);
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'new-camp' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'new-camp' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <PlusCircle size={20} />
              <span style={styles.mobileMenuNavLabel}>New Camp</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('camp-list');
                setShowCampList(true);
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'camp-list' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'camp-list' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Tent size={20} />
              <span style={styles.mobileMenuNavLabel}>Camp List</span>
            </motion.button>
            
            <motion.button
              onClick={() => {
                setActiveSection('Lernenden-Check');
                setShowMobileMenu(false);
              }}
              style={{
                ...styles.mobileMenuNavButton,
                backgroundColor: activeSection === 'Lernenden-Check' ? 'rgba(230, 55, 110, 0.1)' : 'transparent',
                color: activeSection === 'Lernenden-Check' ? accentColor : styles.mobileMenuNavLabel.color,
              }}
              whileHover={{ scale: 1.03, x: 5 }}
              whileTap={{ scale: 0.97 }}
            >
              <Shield size={20} />
              <span style={styles.mobileMenuNavLabel}>User Verification</span>
            </motion.button>
          </div>
          
          <div style={styles.mobileMenuFooter}>
            <div style={styles.mobileMenuDivider}></div>
            
            <div style={styles.mobileMenuSettings}>
              <motion.button 
                style={styles.mobileMenuSettingButton}
                onClick={() => {
                  setProfileModalVisible(true);
                  setShowMobileMenu(false);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <User size={18} />
                <span>Profile</span>
              </motion.button>
              
              
            </div>
            
            <motion.button 
              style={styles.mobileMenuLogoutButton}
              onClick={() => {
                handleLogout();
                setShowMobileMenu(false);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut size={20} />
              <span>Logout</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
  

  // Mobile Bottom Navigation
  const MobileBottomNavigation = () => (
    <div style={{
      position: 'fixed',
      bottom: 30,
      left: "10%",
      right: 0,
      backgroundColor: '#da291c', // Blue/purple color matching the image
      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      zIndex: 100,
      padding: '12px 16px',
      display: 'flex',
      width:"75%",
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '30px 30px 30px 30px', // Curved top corners
    }}>
      <button
        style={{
          background: 'none',
          border: 'none',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          opacity: activeSection === 'home' ? 1 : 0.7,
          transition: 'opacity 0.3s ease',
        }}
        onClick={() => changeSection('home')}
      >
        <Home size={22} />
      </button>
      
      <button
        style={{
          background: 'none',
          border: 'none',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          opacity: activeSection === 'users' ? 1 : 0.7,
          transition: 'opacity 0.3s ease',
        }}
        onClick={() => changeSection('users')}
      >
        <Users size={22} />
      </button>
      
      <button
        style={{
          background: 'none',
          border: 'none',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          opacity: ['new-camp', 'camp-list', 'camp-overview'].includes(activeSection) ? 1 : 0.7,
          transition: 'opacity 0.3s ease',
        }}
        onClick={() => {
          setActiveSection('camp-list');
          // setShowCampList(true);
          setShowCampOverview(true);

        }}
      >
        <Tent size={22} />
      </button>
      
      <button
        style={{
          background: 'none',
          border: 'none',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          opacity: activeSection === 'alerts' ? 1 : 0.7,
          transition: 'opacity 0.3s ease',
        }}
        onClick={() => {
          setActiveSection('alerts');
          setShowAlertModal(true);
        }}
      >
        <Bell size={22} />
      </button>
      
      <button
        style={{
          background: '#ffffff',
          border: 'none',
          color: '#4B56D2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '8px 16px',
          borderRadius: '24px',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
          fontWeight: '500',
          fontSize: '14px',
        }}
        onClick={() => setShowMobileMenu(true)}
      >
        <Menu size={18} />
        <span>Menu</span>
      </button>
    </div>
  );
  
  // Replace Trash component if not available
  const Trash = ({ size, ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );

  // Color picker component
  const ColorPicker = () => {
    const colors = [
      '#e6376e', // Default pink
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F97316', // Orange
      '#8B5CF6', // Purple
      '#14B8A6', // Teal
      '#0EA5E9', // Sky
      '#EC4899', // Pink
      '#EF4444', // Red
    ];
    
    return (
      <motion.div 
        style={styles.colorPicker}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
      >
        <div style={styles.colorPickerHeader}>
          <h3 style={styles.colorPickerTitle}>Theme Color</h3>
          <button 
            style={styles.colorPickerClose}
            onClick={() => setShowColorPicker(false)}
          >
            <X size={16} />
          </button>
        </div>
        <div style={styles.colorPickerContent}>
          <div style={styles.colorPickerSwatches}>
            {colors.map((color) => (
              <button
                key={color}
                style={{
                  ...styles.colorPickerSwatch,
                  backgroundColor: color,
                  boxShadow: color === accentColor ? '0 0 0 2px white, 0 0 0 4px ' + color : 'none',
                }}
                onClick={() => setAccentColor(color)}
              />
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  // If not logged in, show login screen
  if (!isLoggedIn) {
    return (
      <div style={styles.loginBody}>
        {/* Animated background */}
        <div style={styles.loginBackground}>
          <div style={styles.loginBackgroundOverlay} />
          <div style={styles.loginBackgroundGradient} />
          <div style={styles.loginBackgroundPattern} />
        </div>
        
        <AnimatePresence>
          {showWelcomeModal && <WelcomeModal />}
        </AnimatePresence>
        
        <motion.div 
          style={styles.loginContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: pageLoaded ? 1 : 0,
            y: pageLoaded ? 0 : 20
          }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div style={styles.loginLeft}>
            <div style={styles.loginLeftContent}>
              <motion.div 
                style={styles.loginBrandContainer}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <img 
                  src="https://www.sunrise.ch/eshop/HeaderIcons/sunrise_new_logo.svg" 
                  alt="Sunrise Logo" 
                  style={styles.loginLogo} 
                />
              </motion.div>
              
              <motion.h1 
                style={styles.loginWelcomeTitle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Admin Dashboard
              </motion.h1>
              
              <motion.p 
                style={styles.loginWelcomeText}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Welcome to the Sunrise Admin Portal. Please login to access your dashboard.
              </motion.p>
              
              <motion.div 
                style={styles.loginFeatures}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.div 
                  style={styles.loginFeatureItem}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Users style={styles.loginFeatureIcon} size={22} />
                  <span style={styles.loginFeatureText}>User Management</span>
                </motion.div>
                
                <motion.div 
                  style={styles.loginFeatureItem}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <Tent style={styles.loginFeatureIcon} size={22} />
                  <span style={styles.loginFeatureText}>Camp Management</span>
                </motion.div>
                
                <motion.div 
                  style={styles.loginFeatureItem}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <Shield style={styles.loginFeatureIcon} size={22} />
                  <span style={styles.loginFeatureText}>Access Control</span>
                </motion.div>
                
                <motion.div 
                  style={styles.loginFeatureItem}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                >
                  <BarChart2 style={styles.loginFeatureIcon} size={22} />
                  <span style={styles.loginFeatureText}>Analytics & Reports</span>
                </motion.div>
              </motion.div>
              
              {/* <motion.div 
                style={styles.loginImageContainer}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.1 }}
              >
                <img 
                  src="icon.png" 
                  alt="Dashboard Preview"
                  style={styles.loginImage}
                />
              </motion.div> */}
            </div>
          </div>
          
          <div style={styles.loginRight}>
            <motion.div 
              style={styles.loginCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div style={styles.loginHeader}>
                <img 
                  src="https://www.sunrise.ch/eshop/HeaderIcons/sunrise_new_logo.svg" 
                  alt="Logo" 
                  style={styles.loginLogoMobile} 
                />
                <h1 style={styles.loginTitle}>Welcome Back</h1>
                <p style={styles.loginSubtitle}>Sign in to access your admin dashboard</p>
              </div>
              
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div 
                    style={styles.errorAlert}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <X size={20} style={styles.errorIcon} />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleLogin} style={styles.loginForm}>
                <div style={styles.formGroup}>
                  <label style={styles.inputLabel}>Email</label>
                  <div style={{
                    ...styles.inputWrapper,
                    boxShadow: isSearchFocused ? `0 0 0 2px ${accentColor}15` : 'none',
                  }}>
                    <Mail size={20} style={styles.inputIcon} />
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      style={styles.loginInput}
                      required
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.inputLabel}>Password</label>
                  <div style={{
                    ...styles.inputWrapper,
                    boxShadow: isSearchFocused ? `0 0 0 2px ${accentColor}15` : 'none',
                  }}>
                    <Lock size={20} style={styles.inputIcon} />
                    <input
                      type="password"
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      style={styles.loginInput}
                      required
                    />
                  </div>
                </div>
                
                <motion.button 
                  type="submit" 
                  style={{
                    ...styles.loginButton,
                    backgroundColor: accentColor,
                  }}
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loginAnimation && (
                    <div style={styles.loginButtonProgress}></div>
                  )}
                  {isLoading && !loginAnimation ? (
                    <span style={styles.loadingSpinnerContainer}>
                      <Loader size={20} style={styles.loadingSpinner} />
                      <span>Signing in...</span>
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </motion.button>
                
                <p style={styles.loginNote}>
                  <HelpCircle size={16} style={{marginRight: '8px'}} />
                  For administrator access only. Contact Neo Stutz at <a href="mailto:neo.stutz@sunrise.net" style={styles.loginLink}>neo.stutz@sunrise.net</a> for support.
                </p>
              </form>
            </motion.div>
            
            <motion.div 
              style={styles.loginRightFooter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              <p style={styles.loginCopyright}>
                © {new Date().getFullYear()} Sunrise Communications AG. All rights reserved.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main Dashboard - Desktop or Mobile view
  return (
    <div style={{
      
      ...styles.dashboardContainer,
      opacity: pageLoaded ? 1 : 0,
      transition: 'opacity 0.5s ease-in-out'
    }}>
      {/* Welcome modal */}
      <AnimatePresence>
        {showWelcomeModal && <WelcomeModal />}
      </AnimatePresence>
      
      {/* Toast for success messages */}
      <AnimatePresence>
        {showSuccessToast && (
          <SuccessToast message={successToastMessage} />
        )}
      </AnimatePresence>
      
      {/* Page transition overlay */}
      <AnimatePresence>
        {pageTransition && (
          <motion.div 
            style={styles.pageTransitionOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
      
      {/* Mobile Layout */}
      {isMobile ? (
        <>
          {/* Mobile Top Bar */}
          <motion.div 
            style={styles.mobileTopBar}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.div 
              style={styles.mobileBrand}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src="https://www.sunrise.ch/eshop/HeaderIcons/sunrise_new_logo.svg" 
                alt="Sunrise" 
                style={styles.mobileLogo} 
              />
            </motion.div>
            
            <div style={styles.mobileTopBarRight}>
              <motion.button 
                style={styles.mobileNotificationButton}
                onClick={() => setShowNotifications(!showNotifications)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <motion.span 
                    style={{
                      ...styles.mobileNotificationBadge,
                      backgroundColor: accentColor,
                    }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                  >
                    {notificationCount}
                  </motion.span>
                )}
              </motion.button>
              
              <motion.img 
                src={profileImage || '/api/placeholder/40/40'}
                alt="Profile"
                style={styles.mobileProfileImage}
                onClick={() => setProfileModalVisible(true)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              />
            </div>
          </motion.div>
          
          {/* Mobile Content Area */}
          <motion.div 
            style={{
              ...styles.mobileContent,
              opacity: contentAnimation ? 1 : 0,
              transform: contentAnimation ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              paddingBottom: '80px', // Add padding for bottom navigation
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
           {activeSection === 'home' && (
  <DashboardHome 
    onNavigate={(section) => {
      // Implement navigation logic directly in this method
      switch(section) {
        case 'users':
          setActiveSection('users');
          setShowCampList(false);
          setShowCampOverview(false);
          break;
        case 'alerts':
          setActiveSection('alerts');
          setShowAlertModal(true);
          break;
        case 'camps':
          setActiveSection('camp-list');
          setShowCampOverview(true);
          break;
        case 'verification':
          setActiveSection('Lernenden-Check');
          break;
        case 'history':
          setActiveSection('history');
          setShowAllHistory(true);
          break;
        case 'settings':
          setActiveSection('Lernenden-Check');
          break;
        default:
          console.warn(`Unhandled navigation section: ${section}`);
      }
    }} 
  />
)}

            
            {activeSection === 'users' && (
              <div style={styles.mobileSectionContainer}>
                <div style={styles.mobileSectionHeader}>
                  <h2 style={styles.mobileSectionTitle}>User Management</h2>
                  <div style={styles.mobileSearchContainer}>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={styles.mobileSearchInput}
                    />
                    <Search size={18} style={styles.mobileSearchIcon} />
                  </div>
                </div>
                
                <div style={styles.mobileFilters}>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={styles.mobileFilterSelect}
                  >
                    <option value="all">All Status</option>
                    <option value="CHECKEDOUT">Checked Out</option>
                    <option value="Nicht-Abgemeldet">Active</option>
                  </select>
                  
                  <div style={styles.mobileViewToggle}>
                    <button
                      style={{
                        ...styles.mobileViewToggleButton,
                        backgroundColor: currentView === 'grid' ? accentColor : 'transparent',
                        color: currentView === 'grid' ? '#fff' : '#666',
                      }}
                      onClick={() => setCurrentView('grid')}
                    >
                      <Grid size={16} />
                    </button>
                    <button
                      style={{
                        ...styles.mobileViewToggleButton,
                        backgroundColor: currentView === 'list' ? accentColor : 'transparent',
                        color: currentView === 'list' ? '#fff' : '#666',
                      }}
                      onClick={() => setCurrentView('list')}
                    >
                      <List size={16} />
                    </button>
                  </div>
                </div>
                
                <div style={styles.mobileUserContainer}>
                  {currentView === 'grid' ? (
                    <div style={styles.mobileUserGrid}>
                      {filteredUsers.map((user, index) => (
                        <motion.div 
                          key={user.email} 
                          style={styles.mobileUserCard}
                          onClick={() => handleUserClick(user)}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 * Math.min(index, 5) }}
                          whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                        >
                          <div style={styles.mobileUserCardHeader}>
                            <div style={styles.mobileUserInfo}>
                              <img
                                src={user.profileImagePath}
                                alt={user.username}
                                style={styles.mobileUserAvatar}
                              /><div style={styles.mobileUserDetails}>
                              <h3 style={styles.mobileUserName}>{user.username}</h3>
                              <p style={styles.mobileUserEmail}>{user.email}</p>
                            </div>
                          </div>
                          <span
                            style={{
                              ...styles.mobileStatusBadge,
                              backgroundColor:
                                user.status === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                            }}
                          >
                            {user.status === 'CHECKEDOUT' ? 'Absent' : 'Active'}
                          </span>
                        </div>
                        <div style={styles.mobileUserActions}>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUserHistory(user);
                            }}
                            style={styles.mobileActionButton}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Clock size={16} style={styles.mobileButtonIcon} />
                            <span>History</span>
                          </motion.button>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateUserStatus(
                                user.email,
                                user.status === 'CHECKEDOUT'
                                  ? 'Nicht-Abgemeldet'
                                  : 'CHECKEDOUT'
                              );
                            }}
                            style={{
                              ...styles.mobileActionButton,
                              backgroundColor:
                                user.status === 'CHECKEDOUT' ? '#ef4444' : '#10b981',
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {user.status === 'CHECKEDOUT' ? (
                              <>
                                <User size={16} style={styles.mobileButtonIcon} />
                                <span>Set Active</span>
                              </>
                            ) : (
                              <>
                                <LogOut size={16} style={styles.mobileButtonIcon} />
                                <span>Set Absent</span>
                              </>
                            )}
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.mobileUserList}>
                    {filteredUsers.map((user, index) => (
                      <motion.div 
                        key={user.email} 
                        style={styles.mobileUserListItem}
                        onClick={() => handleUserClick(user)}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.05 * Math.min(index, 10) }}
                        whileHover={{ backgroundColor: 'rgba(230, 55, 110, 0.05)' }}
                      >
                        <div style={styles.mobileUserListInfo}>
                          <img
                            src={user.profileImagePath}
                            alt={user.username}style={styles.mobileUserListAvatar}
                          />
                          <div style={styles.mobileUserListDetails}>
                            <h3 style={styles.mobileUserListName}>{user.username}</h3>
                            <p style={styles.mobileUserListEmail}>{user.email}</p>
                          </div>
                        </div>
                        <span
                          style={{
                            ...styles.mobileStatusBadgeSmall,
                            backgroundColor:
                              user.status === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                          }}
                        >
                          {user.status === 'CHECKEDOUT' ? 'Absent' : 'Active'}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeSection === 'history' && showAllHistory && (
            <div style={styles.mobileSectionContainer}>
              <div style={styles.mobileSectionHeader}>
                <h2 style={styles.mobileSectionTitle}>Activity History</h2>
                <div style={styles.mobileSearchContainer}>
                  <input
                    type="text"
                    placeholder="Search history..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={styles.mobileSearchInput}
                  />
                  <Search size={18} style={styles.mobileSearchIcon} />
                </div>
              </div>
              
              <div style={styles.mobileHistoryList}>
                {groupedHistory.map(([date, entries], groupIndex) => (
                  <motion.div 
                    key={groupIndex} 
                    style={styles.mobileHistoryGroup}
                    className={expandedDays[date] ? 'expanded' : ''}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * Math.min(groupIndex, 5) }}
                  >
                    <motion.div 
                      style={styles.mobileHistoryGroupHeader}
                      onClick={() => toggleDay(date)}
                      whileHover={{ backgroundColor: 'rgba(230, 55, 110, 0.05)' }}
                    >
                      <h3 style={styles.mobileHistoryDate}>{date}</h3>
                      <motion.button 
                        style={styles.mobileToggleButton}
                        animate={{ rotate: expandedDays[date] ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown size={20} />
                      </motion.button>
                    </motion.div>
                    
                    <div
                      ref={(el) => (contentRefs.current[date] = el)}
                      style={{
                        ...styles.mobileHistoryContent,
                        maxHeight: expandedDays[date] ? '2000px' : '0px',
                      }}
                    >
                      {entries.map((entry, index) => (
                        <motion.div 
                          key={index} 
                          style={styles.mobileHistoryItem}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: expandedDays[date] ? 1 : 0, y: expandedDays[date] ? 0 : 10 }}
                          transition={{ duration: 0.3, delay: 0.05 * Math.min(index, 10) }}
                        >
                          <div style={styles.mobileHistoryItemHeader}>
                            <span style={styles.mobileHistoryTime}>{entry.timeAgo}</span>
                            <span 
                              style={{
                                ...styles.mobileHistoryStatus,
                                backgroundColor: entry.newStatus === 'CHECKEDOUT' ? '#10b981' : '#ef4444'
                              }}
                            >
                              {entry.newStatus === 'CHECKEDOUT' ? 'Checked Out' : 'Active'}
                            </span>
                          </div>
                          <div style={styles.mobileHistoryItemBody}>
                            <p style={styles.mobileHistoryText}>
                              <strong>{entry.username}</strong> status changed from <strong>{entry.oldStatus}</strong> to <strong>{entry.newStatus}</strong> by <strong>{entry.updatedBy}</strong>
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
          
          {activeSection === 'Lernenden-Check' && (
            <div style={styles.mobileSectionContainer}>
              <div style={styles.mobileSectionHeader}>
                <h2 style={styles.mobileSectionTitle}>User Verification</h2>
              </div>
              
              <motion.div 
                style={styles.mobileSettingsContainer}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p style={styles.mobileSettingsDescription}>
                  Enter usernames (one per line) to check if they exist in the system.
                </p>
                <textarea
                  value={bulkUserInput}
                  onChange={(e) => setBulkUserInput(e.target.value)}
                  placeholder="Usernames (one per line)"
                  style={styles.mobileBulkTextarea}
                />
                <motion.button 
                  onClick={handleBulkUserCheck} 
                  style={{
                    ...styles.mobileCheckButton,
                    backgroundColor: accentColor,
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Search size={18} style={{ marginRight: '8px' }} />
                  Verify Users
                </motion.button>
                
                <AnimatePresence>
                  {missingUsers.length > 0 && (
                    <motion.div 
                      style={styles.mobileMissingUsers}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 style={styles.mobileMissingTitle}>Missing Users</h3>
                      <motion.ul 
                        style={styles.mobileMissingList}
                        initial="hidden"
                        animate="visible"
                        variants={{
                          visible: { 
                            transition: { staggerChildren: 0.1 } 
                          },
                          hidden: {}
                        }}
                      >
                        {missingUsers.map(username => (
                          <motion.li 
                            key={username} 
                            style={styles.mobileMissingItem}
                            variants={{
                              visible: { opacity: 1, x: 0 },
                              hidden: { opacity: 0, x: -20 }
                            }}
                          >
                            {username}
                          </motion.li>
                        ))}
                      </motion.ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          )}
          
          {activeSection === 'alerts' && (
            <AlertManagementScreen 
              isVisible={true}
              onClose={() => {
                setShowAlertModal(false);
                setActiveSection('home');
              }}
            />
          )}
          
          {activeSection === 'room-assignment' && (
            <EnhancedRoomAssignment 
              users={users} 
              fetchUsers={fetchUsers}
            />
          )}
          
          {activeSection === 'new-camp' && (
            <CampManagementForm 
              onClose={() => {
                setShowCampForm(false);
                setActiveSection('home');
              }}
              initialData={existingCampData}
            />
          )}
          
          {activeSection === 'camp-overview' && (
            <div style={styles.mobileModalContainer}>
              <CampOverview />
            </div>
          )}
          
          {activeSection === 'camp-list' && (
            <div style={styles.mobileModalContainer}>
              <CampList />
            </div>
          )}
        </motion.div>
        
        {/* Mobile Notifications Dropdown */}
        <AnimatePresence>
          {showNotifications && (
            <div style={styles.mobileNotificationsContainer}>
              <NotificationsDropdown />
            </div>
          )}
        </AnimatePresence>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && <MobileMenu />}
        </AnimatePresence>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNavigation />
      </>
    ) : (
      // Desktop Layout
      <>
        {/* Desktop Sidebar */}
        <motion.div
          style={{
            ...styles.sidebar,
            width: isCollapsed ? '80px' : '280px',
          }}
          initial={{ x: -280 }}
          animate={{ x: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          onMouseEnter={() => !isCollapsed && setSidebarHover(true)}
          onMouseLeave={() => setSidebarHover(false)}
        >
          <motion.div 
            style={styles.sidebarHeader}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.button
              onClick={() => setIsCollapsed(!isCollapsed)}
              style={styles.collapseButton}
              whileHover={{ rotate: 180 }}
              whileTap={{ scale: 0.9 }}
            >
              <Menu size={24} />
            </motion.button>
            {!isCollapsed && (
              <motion.div 
                style={styles.brandContainer}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <img
                  src="https://www.sunrise.ch/eshop/HeaderIcons/sunrise_new_logo.svg"
                  alt="Sunrise"
                  style={styles.brandLogo}
                />
                <h1 style={styles.sidebarTitle}>Admin</h1>
              </motion.div>
            )}
          </motion.div>
        
          <motion.div 
            style={styles.profileSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <motion.div 
              style={styles.profileImageContainer}
              onClick={() => setProfileModalVisible(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <img
                src={profileImage || '/api/placeholder/80/80'}
                alt="Profile"
                style={styles.profilePicture}
              />
              <motion.button 
                style={styles.editProfileButton}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.5 }}
              >
                <Settings size={16} color="#fff" />
              </motion.button>
            </motion.div>
            {!isCollapsed && (
              <motion.div 
                style={styles.profileInfo}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <h3 style={styles.profileName}>{username || 'Admin'}</h3>
                <span style={styles.profileRole}>Administrator</span>
              </motion.div>
            )}
          </motion.div>
        
          <div style={styles.sidebarDivider}></div>
        
          <motion.nav 
            style={styles.navigation}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <motion.div 
              style={styles.navSection}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              {!isCollapsed && (
                <motion.h3 
                  style={styles.navSectionTitle}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  Dashboard
                </motion.h3>
              )}
              <motion.button
                onClick={() => changeSection('home')}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'home' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'home' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.8 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Home size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>Home</span>}
              </motion.button>
              <motion.div 
            style={styles.navSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1 }}
          >
            {!isCollapsed && (
              <motion.h3 
                style={styles.navSectionTitle}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.1 }}
              >
                Dateiverwaltung
              </motion.h3>
            )}
            
            <motion.button
              onClick={() => changeSection('files')}
              style={{
                ...styles.navButton,
                backgroundColor: activeSection === 'files' ? 'rgb(245, 247, 251)' : 'transparent',
                color: activeSection === 'files' ? '#000' : 'rgba(255, 255, 255, 0.7)',
              }}
              whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 1.2 }}
            >
              <motion.div 
                style={styles.navIconContainer}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FileText size={22} />
              </motion.div>
              {!isCollapsed && <span style={styles.navLabel}>Dateien</span>}
            </motion.button>
          </motion.div>
              <motion.button
              onClick={() => changeSection('groups')}
              style={{
                ...styles.navButton,
                backgroundColor: activeSection === 'groups' ? 'rgb(245, 247, 251)' : 'transparent',
                color: activeSection === 'groups' ? '#000' : 'rgba(255, 255, 255, 0.7)',
              }}
              whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 1.3 }}
            >
              <motion.div 
                style={styles.navIconContainer}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Users size={22} />
              </motion.div>
              {!isCollapsed && <span style={styles.navLabel}>Group Management</span>}
            </motion.button>
              <motion.button
                onClick={() => changeSection('users')}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'users' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'users' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.85 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Users size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>User Management</span>}
              </motion.button>
            
              <motion.button
                onClick={() => {
                  changeSection('history');
                  setShowAllHistory(true);
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'history' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'history' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.9 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Clock size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>History</span>}
              </motion.button>
            </motion.div>
          
            <motion.div 
              style={styles.navSection}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              {!isCollapsed && (
                <motion.h3 
                  style={styles.navSectionTitle}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                >
                  Camp Management
                </motion.h3>
              )}
              <motion.button
                onClick={() => {
                  changeSection('new-camp');
                  setShowCampForm(true);
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'new-camp' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'new-camp' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.2 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <PlusCircle size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>New Camp</span>}
              </motion.button>
            
              <motion.button
                onClick={() => {
                  setActiveSection('camp-overview');
                  setShowCampOverview(true);
                
          
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'camp-overview' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'camp-overview' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.25 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Layout size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>Camp Overview</span>}
              </motion.button>
            
              <motion.button
                onClick={() => {
                  changeSection('camp-list');
                  setShowCampList(true);
                  setShowCampOverview(false);
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'camp-list' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'camp-list' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.3 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Tent size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>Camp List</span>}
              </motion.button>
            
              <motion.button
                onClick={() => {
                  changeSection('room-assignment');
                  setShowCampList(false);
                  setShowCampOverview(false);
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'room-assignment' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'room-assignment' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.35 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Home size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>Room Assignment</span>}
              </motion.button>
            </motion.div>
          
            <motion.div 
              style={styles.navSection}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.4 }}
            >
              {!isCollapsed && (
                <motion.h3 
                  style={styles.navSectionTitle}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 1.5 }}
                >
                  System
                </motion.h3>
              )}
              <motion.button
                onClick={() => changeSection('Lernenden-Check')}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'Lernenden-Check' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'Lernenden-Check' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.55 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Shield size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>User Verification</span>}
              </motion.button>
            
              <motion.button
                onClick={() => {
                  changeSection('alerts');
                  setShowAlertModal(true);
                }}
                style={{
                  ...styles.navButton,
                  backgroundColor: activeSection === 'alerts' ? 'rgb(245, 247, 251)' : 'transparent',
                  color: activeSection === 'alerts' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                }}
                whileHover={{ x: isCollapsed ? 0 : 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.6 }}
              >
                <motion.div 
                  style={styles.navIconContainer}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Bell size={22} />
                </motion.div>
                {!isCollapsed && <span style={styles.navLabel}>Alert Management</span>}
              </motion.button>
              
              {!isCollapsed && (
                <motion.button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  style={{
                    ...styles.navButton,
                    backgroundColor: showColorPicker ? 'rgb(245, 247, 251)' : 'transparent',
                    color: showColorPicker ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                  }}
                  whileHover={{ x: 5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 1.65 }}
                >
                  <motion.div 
                    style={{
                      ...styles.navIconContainer,
                      backgroundColor: accentColor + '40',
                      borderRadius: '6px',
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Heart size={22} />
                  </motion.div>
                  <span style={styles.navLabel}>Theme</span>
                </motion.button>
              )}
              
              
            </motion.div>
          </motion.nav>
        
          <motion.div 
            style={styles.sidebarFooter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            <motion.button 
              style={styles.logoutButton}
              onClick={handleLogout}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut size={20} />
              {!isCollapsed && <span>Logout</span>}
            </motion.button>
          </motion.div>
          
          {/* Color picker */}
          <AnimatePresence>
            {showColorPicker && !isCollapsed && (
              <div style={styles.colorPickerContainer}>
                <ColorPicker />
              </div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Desktop Main Content */}
        <motion.div
          style={{
            ...styles.mainContent,
            marginLeft: isCollapsed ? '80px' : '280px',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <motion.div 
            style={styles.topBar}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div style={styles.pageInfo}>
              <motion.h1 
                style={styles.pageTitle}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {activeSection === 'home'
                  ? 'Dashboard'
                  : activeSection === 'users'
                  ? 'User Management'
                  : activeSection === 'history'
                  ? 'History' 
                  : activeSection === 'groups'
                  ? 'Groups' 
                  : activeSection === 'Lernenden-Check'
                  ? 'User Verification' 
                  : activeSection === 'alerts'
                  ? 'Alert Management' 
                  : activeSection === 'new-camp'
                  ? 'New Camp' 
                  : activeSection === 'camp-list'
                  ? 'Camp List'
                  : activeSection === 'camp-overview'
                  ? 'Camp Overview'
                  : activeSection === 'files'
                  ? 'Dateiverwaltung' 
                  : activeSection === 'room-assignment'
                  ? 'Room Assignment'
                  : 'Admin Dashboard'}
              </motion.h1>
              <motion.p 
                style={styles.pageBreadcrumb}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <span>Dashboard</span>
                <span style={styles.breadcrumbSeparator}>/</span>
                <span style={{
                  ...styles.currentPage,
                  color: accentColor,
                }}>
                  {activeSection === 'home'
                    ? 'Home'
                    : activeSection === 'users'
                    ? 'User Management'
                    : activeSection === 'groups'
                    ? 'Groups' 
                    : activeSection === 'files'
                    ? 'Dateiverwaltung' 
                    : activeSection === 'history'
                    ? 'History' 
                    : activeSection === 'Lernenden-Check'
                    ? 'User Verification' 
                    : activeSection === 'alerts'
                    ? 'Alert Management' 
                    : activeSection === 'new-camp'
                    ? 'New Camp' 
                    : activeSection === 'camp-list'
                    ? 'Camp List'
                    : activeSection === 'camp-overview'
                    ? 'Camp Overview'
                    : activeSection === 'room-assignment'
                    ? 'Room Assignment'
                    : 'Admin'}
                </span>
              </motion.p>
              
            </div>
            <div style={styles.topBarActions}>
              <motion.div 
                style={styles.searchContainer}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Search size={20} style={styles.searchIcon} />
                <motion.input
                  type="text"
                  placeholder="Search..."
                  value={activeSection === 'history' ? searchQuery : searchText}
                  onChange={(e) => activeSection === 'history' ? setSearchQuery(e.target.value) : setSearchText(e.target.value)}
                  style={{
                    ...styles.searchInput,
                    boxShadow: isSearchFocused 
                      ? `0 0 0 2px ${accentColor}15` 
                      : styles.searchInput.boxShadow,
                  }}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  whileFocus={{ boxShadow: `0 0 0 2px ${accentColor}30` }}
                />
              </motion.div>
              
              <div style={styles.actionsContainer}>
                <motion.div 
                  style={styles.notificationContainer}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <motion.button 
                    style={styles.notificationButton}
                    onClick={() => setShowNotifications(!showNotifications)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Bell size={20} />
                    {notificationCount > 0 && (
                      <motion.span 
                        style={{
                          ...styles.notificationBadge,
                          backgroundColor: accentColor,
                        }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ 
                          type: 'spring', 
                          stiffness: 500, 
                          damping: 15,
                          delay: 1.2
                        }}
                      >
                        {notificationCount}
                      </motion.span>
                    )}
                  </motion.button>
                  <AnimatePresence>
                    {showNotifications && (
                      <div style={styles.notificationsContainer}>
                        <NotificationsDropdown />
                      </div>
                    )}
                  </AnimatePresence>
                </motion.div>
                <motion.div 
                  style={styles.profileDropdown}
                  onClick={() => setProfileModalVisible(true)}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img
                    src={profileImage || '/api/placeholder/40/40'}
                    alt="Profile"
                    style={styles.profileDropdownImage}
                  />
                  <span style={styles.profileDropdownName}>{username || 'Admin'}</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        
          <div style={{
            ...styles.contentWrapper,
            opacity: contentAnimation ? 1 : 0,
            transform: contentAnimation ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
          }}>
            {/* Home Dashboard */}
            {activeSection === 'home' && (
  <DashboardHome 
    onNavigate={(section) => {
      // Implement navigation logic directly in this method
      switch(section) {
        case 'users':
          setActiveSection('users');
          setShowCampList(false);
          setShowCampOverview(false);
          break;
        case 'alerts':
          setActiveSection('alerts');
          setShowAlertModal(true);
          break;
          
        case 'camps':
          setActiveSection('camp-list');
          setShowCampOverview(true);
          break;
        case 'verification':
          setActiveSection('Lernenden-Check');
          break;
        case 'history':
          setActiveSection('history');
          setShowAllHistory(true);
          break;
        case 'settings':
          setActiveSection('Lernenden-Check');
          break;
        default:
          console.warn(`Unhandled navigation section: ${section}`);
      }
    }} 
  />
)}
        
            {/* User Management */}
            {activeSection === 'users' && (
              <motion.div 
                style={styles.contentArea}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div style={styles.contentHeader}>
                  <div style={styles.headerLeft}>
                    <h2 style={styles.contentTitle}>User List</h2>
                    <p style={styles.contentDescription}>
                      Manage user accounts and status updates
                    </p>
                  </div>
                  <div style={styles.headerRight}>
                    <div style={styles.viewSwitcher}>
                      <button
                        style={{
                          ...styles.viewSwitcherButton,
                          backgroundColor: currentView === 'grid' ? accentColor : 'transparent',
                          color: currentView === 'grid' ? '#fff' : '#666',
                        }}
                        onClick={() => setCurrentView('grid')}
                      >
                        <Grid size={16} />
                      </button>
                      <button
                        style={{
                          ...styles.viewSwitcherButton,
                          backgroundColor: currentView === 'list' ? accentColor : 'transparent',
                          color: currentView === 'list' ? '#fff' : '#666',
                        }}
                        onClick={() => setCurrentView('list')}
                      >
                        <List size={16} />
                      </button>
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={styles.filterSelect}
                    >
                      <option value="all">All Status</option>
                      <option value="CHECKEDOUT">Checked Out</option>
                      <option value="Nicht-Abgemeldet">Active</option>
                    </select>
                  </div>
                </div>
              
                {currentView === 'grid' ? (
                  <motion.div 
                    style={styles.usersGrid}
                    initial="hidden"
                    animate="visible"
                    variants={{
                      visible: { 
                        transition: { staggerChildren: 0.05 } 
                      },
                      hidden: {}
                    }}
                  >
                    {filteredUsers.map((user) => (
                      <motion.div 
                        key={user.email} 
                        style={styles.userCard}
                        onClick={() => handleUserClick(user)}
                        variants={{
                          visible: { opacity: 1, y: 0 },
                          hidden: { opacity: 0, y: 20 }
                        }}
                        whileHover={{ y: -5, boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)' }}
                      >
                        <div style={styles.userCardHeader}>
                          <div style={styles.userImage}>
                            <img
                              src={user.profileImagePath}
                              alt={user.username}
                              style={styles.userAvatar}
                            />
                          </div>
                          <span
                            style={{
                              ...styles.statusBadge,
                              backgroundColor:
                                user.status === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                            }}
                          >
                            {user.status === 'CHECKEDOUT' ? 'Absent' : 'Active'}
                          </span>
                        </div>
                        <div style={styles.userCardBody}>
                          <h3 style={styles.userCardName}>{user.username}</h3>
                          <p style={styles.userCardEmail}>{user.email}</p>
                        </div>
                        <div style={styles.userCardActions}>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUserHistory(user);
                            }}
                            style={styles.userCardButton}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Clock size={16} style={styles.buttonIcon} />
                            <span>History</span>
                          </motion.button>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateUserStatus(
                                user.email,
                                user.status === 'CHECKEDOUT'
                                  ? 'Nicht-Abgemeldet'
                                  : 'CHECKEDOUT'
                              );
                            }}
                            style={{
                              ...styles.userCardButton,
                              backgroundColor:
                                user.status === 'CHECKEDOUT' ? '#ef4444' : '#10b981',
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {user.status === 'CHECKEDOUT' ? (
                              <>
                                <User size={16} style={styles.buttonIcon} />
                                <span>Set Active</span>
                              </>
                            ) : (
                              <>
                                <LogOut size={16} style={styles.buttonIcon} />
                                <span>Set Absent</span>
                              </>
                            )}
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead style={styles.tableHead}>
                        <tr>
                          <th 
                            style={styles.tableHeader} 
                            onClick={() => handleSort('username')}
                          >
                            <div style={styles.headerContent}>
                              <span>Username</span>
                              <div style={styles.sortIndicator}>
                                {sortConfig.key === 'username' && (
                                  sortConfig.direction === 'asc' ? '↑' : '↓'
                                )}
                              </div>
                            </div>
                          </th>
                          <th 
                            style={styles.tableHeader} 
                            onClick={() => handleSort('email')}
                          >
                            <div style={styles.headerContent}>
                              <span>Email</span>
                              <div style={styles.sortIndicator}>
                                {sortConfig.key === 'email' && (
                                  sortConfig.direction === 'asc' ? '↑' : '↓'
                                )}
                              </div>
                            </div>
                          </th>
                          <th 
                            style={styles.tableHeader} 
                            onClick={() => handleSort('status')}
                          >
                            <div style={styles.headerContent}>
                              <span>Status</span>
                              <div style={styles.sortIndicator}>
                                {sortConfig.key === 'status' && (
                                  sortConfig.direction === 'asc' ? '↑' : '↓'
                                )}
                              </div>
                            </div>
                          </th>
                          <th style={styles.tableHeader}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user, index) => (
                          <motion.tr 
                            key={user.email} 
                            style={styles.tableRow}
                            onClick={() => handleUserClick(user)}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.05 * Math.min(index, 20) }}
                            whileHover={{ backgroundColor: 'rgba(230, 55, 110, 0.05)' }}
                          >
                            <td style={styles.tableCell}>
                              <div style={styles.userInfo}>
                                <img
                                  src={user.profileImagePath}
                                  alt={user.username}
                                  style={styles.userAvatar}
                                />
                                <span style={styles.userName}>{user.username}</span>
                              </div>
                            </td>
                            <td style={styles.tableCell}>{user.email}</td>
                            <td style={styles.tableCell}>
                              <span
                                style={{
                                  ...styles.statusBadge,
                                  backgroundColor:
                                    user.status === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                                }}
                              >
                                {user.status === 'CHECKEDOUT' ? 'Absent' : 'Active'}
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              <div style={styles.actionButtons}>
                                <motion.button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewUserHistory(user);
                                  }}
                                  style={styles.actionButton}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Clock size={16} style={styles.buttonIcon} />
                                  <span>History</span>
                                </motion.button>
                                <motion.button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateUserStatus(
                                      user.email,
                                      user.status === 'CHECKEDOUT'
                                        ? 'Nicht-Abgemeldet'
                                        : 'CHECKEDOUT'
                                    );
                                  }}
                                  style={{
                                    ...styles.actionButton,
                                    backgroundColor:
                                      user.status === 'CHECKEDOUT' ? '#ef4444' : '#10b981',
                                  }}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {user.status === 'CHECKEDOUT' ? (
                                    <>
                                      <User size={16} style={styles.buttonIcon} />
                                      <span>Set Active</span>
                                    </>
                                  ) : (
                                    <>
                                      <LogOut size={16} style={styles.buttonIcon} />
                                      <span>Set Absent</span>
                                    </>
                                  )}
                                </motion.button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}{/* History View */}
            {activeSection === 'groups' && (
  <motion.div 
    style={styles.contentArea}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <GroupManagement />
  </motion.div>
)}

{activeSection === 'history' && showAllHistory && (
  <motion.div 
    style={styles.contentArea}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <div style={styles.contentHeader}>
      <div style={styles.headerLeft}>
        <h2 style={styles.contentTitle}>History Log</h2>
        <p style={styles.contentDescription}>
          View chronological status changes and user activity
        </p>
      </div>
      <div style={styles.headerRight}>
        <div style={styles.historyDatePicker}>
          <CalendarIcon size={16} style={styles.historyDatePickerIcon} />
          <select style={styles.historyDatePickerSelect}>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>All time</option>
          </select>
        </div>
      </div>
    </div>
  
    <div style={styles.historyContainer}>
      {groupedHistory.map(([date, entries], groupIndex) => (
        <motion.div 
          key={groupIndex} 
          style={styles.dateGroup}
          className={expandedDays[date] ? 'expanded' : ''}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 * Math.min(groupIndex, 5) }}
        >
          <motion.div 
            style={{
              ...styles.dateHeader,
              backgroundColor: accentColor,
            }}
            onClick={() => toggleDay(date)}
            whileHover={{ backgroundColor: adjustColor(accentColor, -10) }}
          >
            <h3 style={styles.dateTitle}>{date}</h3>
            <motion.button 
              style={styles.toggleButton}
              animate={{ rotate: expandedDays[date] ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={20} style={styles.toggleIcon} />
            </motion.button>
          </motion.div>
        
          <div
            ref={(el) => (contentRefs.current[date] = el)}
            style={{
              ...styles.dateContent,
              maxHeight: expandedDays[date] ? '2000px' : '0px',
            }}
          >
            <table style={styles.historyTable}>
              <thead style={styles.historyTableHead}>
                <tr>
                  <th style={styles.historyTh}>Time</th>
                  <th style={styles.historyTh}>Old Status</th>
                  <th style={styles.historyTh}>New Status</th>
                  <th style={styles.historyTh}>Changed By</th>
                  <th style={styles.historyTh}>User</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <motion.tr 
                    key={index} 
                    style={styles.historyRow}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: expandedDays[date] ? 1 : 0, y: expandedDays[date] ? 0 : 10 }}
                    transition={{ duration: 0.3, delay: 0.05 * Math.min(index, 10) }}
                    whileHover={{ backgroundColor: 'rgba(230, 55, 110, 0.05)' }}
                  >
                    <td style={styles.historyTd}>{entry.timeAgo}</td>
                    <td style={styles.historyTd}>
                      <span style={{
                        ...styles.historyStatus,
                        backgroundColor: entry.oldStatus === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                      }}>
                        {entry.oldStatus}
                      </span>
                    </td>
                    <td style={styles.historyTd}>
                      <span style={{
                        ...styles.historyStatus,
                        backgroundColor: entry.newStatus === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
                      }}>
                        {entry.newStatus}
                      </span>
                    </td>
                    <td style={styles.historyTd}>{entry.updatedBy}</td>
                    <td style={styles.historyTd}>{entry.username}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      ))}
    </div>
  </motion.div>
)}

{/* User Verification */}
{activeSection === 'Lernenden-Check' && (
  <motion.div 
    style={styles.contentArea}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <div style={styles.contentHeader}>
      <div style={styles.headerLeft}>
        <h2 style={styles.contentTitle}>User Verification</h2>
        <p style={styles.contentDescription}>
          Check if users exist in the system
        </p>
      </div>
    </div>
  
    <motion.div 
      style={styles.settingsContainer}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      <div style={styles.settingsSection}>
        <h3 style={styles.settingsTitle}>Bulk User Check</h3>
        <p style={styles.settingsDescription}>
          Enter a list of usernames (one per line) to verify if they exist in the system.
        </p>
        <textarea
          value={bulkUserInput}
          onChange={(e) => setBulkUserInput(e.target.value)}
          placeholder="Enter usernames (one per line)"
          style={styles.bulkTextarea}
        />
        <motion.button 
          onClick={handleBulkUserCheck} 
          style={{
            ...styles.verifyButton,
            backgroundColor: accentColor,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Search size={18} style={{marginRight: '8px'}} />
          Verify Users
        </motion.button>
      </div>
    
      <AnimatePresence>
        {missingUsers.length > 0 && (
          <motion.div 
            style={styles.resultSection}
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: '30px' }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 style={styles.resultTitle}>Missing Users</h3>
            <p style={styles.resultDescription}>
              The following {missingUsers.length} users were not found in the system:
            </p>
            <motion.div 
              style={styles.missingUsers}
              initial="hidden"
              animate="visible"
              variants={{
                visible: { 
                  transition: { staggerChildren: 0.05 } 
                },
                hidden: {}
              }}
            >
              {missingUsers.map(username => (
                <motion.div 
                  key={username} 
                  style={styles.missingUser}
                  variants={{
                    visible: { opacity: 1, x: 0 },
                    hidden: { opacity: 0, x: -20 }
                  }}
                >
                  <User size={16} style={styles.missingUserIcon} />
                  <span style={styles.missingUserName}>{username}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  </motion.div>
)}

{/* Alert Management */}
{activeSection === 'alerts' && (
  <AlertManagementScreen 
    isVisible={true}
    onClose={() => {
      setShowAlertModal(false);
      setActiveSection('home');
    }}
  />
)}

{/* Room Assignment */}
{activeSection === 'room-assignment' && (
  <EnhancedRoomAssignment 
    users={users} 
    fetchUsers={fetchUsers}
  />
)}

{/* New Camp Form */}
{activeSection === 'new-camp' && (
  <CampManagementForm 
    onClose={() => {
      setShowCampForm(false);
      setActiveSection('home');
    }}
    initialData={existingCampData}
  />
)}
 {activeSection === 'files' && token && (
            <motion.div 
              style={styles.contentArea}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FileManagement token={token} accentColor={accentColor || "#da291c"} />
            </motion.div>
          )}
{/* Camp Overview */}
{activeSection === 'camp-overview' && (
  <motion.div 
    style={styles.modalContainer}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <CampOverview />
  </motion.div>
)}

{/* Camp List */}
{activeSection === 'camp-list' && (
  <motion.div 
    style={styles.modalContainer}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <CampList />
  </motion.div>
)}
</div>
</motion.div>
</>
)}

{/* Common Modals for Both Mobile and Desktop */}
<AnimatePresence>
{selectedUser && (
<div style={styles.modalOverlay}>
<motion.div
initial={{ opacity: 0, scale: 0.9 }}
animate={{ opacity: 1, scale: 1 }}
exit={{ opacity: 0, scale: 0.9 }}
transition={{ duration: 0.3 }}
>
<UserModal
  user={selectedUser}
  onClose={() => setSelectedUser(null)}
  onUpdate={(updatedUser) => {
    fetchUsers();
    setSelectedUser(null);
  }}
/>
</motion.div>
</div>
)}
</AnimatePresence>

{/* Password Change Modal */}
<AnimatePresence>
{passwordModalVisible && (
<div style={styles.modalOverlay}>
<motion.div 
style={styles.passwordModal}
initial={{ opacity: 0, scale: 0.9 }}
animate={{ opacity: 1, scale: 1 }}
exit={{ opacity: 0, scale: 0.9 }}
transition={{ duration: 0.3 }}
>
<div style={styles.modalHeader}>
  <h3 style={styles.modalTitle}>Change Password</h3>
  <motion.button 
    style={styles.modalCloseButton}
    onClick={() => {
      setPasswordModalVisible(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
      setPasswordError('');
    }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
  >
    <X size={24} />
  </motion.button>
</div>
<div style={styles.modalBody}>
  {passwordError && (
    <motion.div 
      style={styles.passwordError}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {passwordError}
    </motion.div>
  )}
  <div style={styles.passwordForm}>
    <div style={styles.passwordFormGroup}>
      <label style={styles.passwordLabel}>New Password</label>
      <input
        type="password"
        style={styles.passwordInput}
        placeholder="Enter new password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
    </div>
    <div style={styles.passwordButtonGroup}>
      <motion.button
        onClick={() => {
          setPasswordModalVisible(false);
          setSelectedUserForPassword(null);
          setNewPassword('');
          setPasswordError('');
        }}
        style={styles.cancelButton}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Cancel
      </motion.button>
      <motion.button
        onClick={() => handleAdminPasswordChange(loginData.email, newPassword)}
        style={{
          ...styles.saveButton,
          backgroundColor: accentColor,
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Change Password
      </motion.button>
    </div>
  </div>
</div>
</motion.div>
</div>
)}
</AnimatePresence>

{/* Profile Edit Modal */}
<AnimatePresence>
{profileModalVisible && (
<ProfileEditModal
isOpen={profileModalVisible}
onClose={() => setProfileModalVisible(false)}
profileImage={profileImage}
username={username}
email={email}
onSave={saveProfileChanges}
onImagePick={pickImage}
/>
)}
</AnimatePresence>
 {activeSection === 'new-camp' && (
            <CampManagementForm 
              onClose={() => {
                setShowCampForm(false);
                setActiveSection('home');
              }}
              initialData={existingCampData}
            />
          )}
{/* History Modal */}
<AnimatePresence>
{showHistoryModal && selectedUser && (
<UserHistoryView
user={selectedUser}
onClose={() => {
setShowHistoryModal(false);
setSelectedUser(null);
}}
/>
)}
</AnimatePresence>
</div>
);
};

// Grid and List components for mobile view
const Grid = ({ size, ...props }) => (
<svg
xmlns="http://www.w3.org/2000/svg"
width={size}
height={size}
viewBox="0 0 24 24"
fill="none"
stroke="currentColor"
strokeWidth="2"
strokeLinecap="round"
strokeLinejoin="round"
{...props}
>
<rect x="3" y="3" width="7" height="7"></rect>
<rect x="14" y="3" width="7" height="7"></rect>
<rect x="14" y="14" width="7" height="7"></rect>
<rect x="3" y="14" width="7" height="7"></rect>
</svg>
);

const List = ({ size, ...props }) => (
<svg
xmlns="http://www.w3.org/2000/svg"
width={size}
height={size}
viewBox="0 0 24 24"
fill="none"
stroke="currentColor"
strokeWidth="2"
strokeLinecap="round"
strokeLinejoin="round"
{...props}
>
<line x1="8" y1="6" x2="21" y2="6"></line>
<line x1="8" y1="12" x2="21" y2="12"></line>
<line x1="8" y1="18" x2="21" y2="18"></line>
<line x1="3" y1="6" x2="3.01" y2="6"></line>
<line x1="3" y1="12" x2="3.01" y2="12"></line>
<line x1="3" y1="18" x2="3.01" y2="18"></line>
</svg>
);

// Define the mobile styles
const mobileStyles = {
dashboardContainer: {
minHeight: '100vh',

backgroundColor: '#f5f7fb',
display: 'flex',
flexDirection: 'column',
fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
position: 'relative',
overflowX: 'hidden',
},

// Mobile Top Bar
mobileTopBar: {
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
padding: '16px',
backgroundColor: '#ffffff',

boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
position: 'sticky',
top: 0,
zIndex: 50,
},

mobileMenuButton: {
padding: '10px',
borderRadius: '10px',
border: 'none',
backgroundColor: 'rgba(230, 55, 110, 0.1)',
color: '#e6376e',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
},

mobileBrand: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
},

mobileLogo: {
height: '24px',
},

mobileTopBarRight: {
display: 'flex',
alignItems: 'center',
gap: '16px',
},

mobileNotificationButton: {
padding: '10px',
borderRadius: '10px',
border: 'none',
backgroundColor: 'rgba(230, 55, 110, 0.1)',
color: '#e6376e',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
position: 'relative',
cursor: 'pointer',
},

mobileNotificationBadge: {
position: 'absolute',
top: '-5px',
right: '-5px',
width: '18px',
height: '18px',
borderRadius: '50%',
backgroundColor: '#e6376e',
color: '#ffffff',
fontSize: '10px',
fontWeight: '600',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
},

mobileProfileImage: {
width: '38px',
height: '38px',
borderRadius: '12px',
objectFit: 'cover',
boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
},

// Mobile Content Container
mobileContent: {
flex: 1,
padding: '16px',
paddingTop: '8px',
paddingBottom: '100px', 
// Extra space at the bottom for iOS
},

// Mobile Bottom Navigation
mobileBottomNavContainer: {
  position: 'fixed',
  bottom: 50,
  left: 0,
  right: 0,
  backgroundColor: '#4B56D2', // Blue/purple color matching the image
  boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
  zIndex: 100,
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '30px 30px 30px 30px', // Curved top corners
},

mobileBottomNav: {
display: 'flex',
justifyContent: 'space-around',
padding: '8px 0',
},

mobileBottomNavButton: {
  background: 'none',
  border: 'none',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
},
mobileBottomNavIcon: {
position: 'relative',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
marginBottom: '4px',
},

mobileBottomNavLabel: {
fontSize: '11px',
marginTop: '2px',
},

mobileBottomNavIndicator: {
position: 'absolute',
bottom: '-8px',
width: '4px',
height: '4px',
borderRadius: '50%',
backgroundColor: 'currentColor',
animation: 'bottomBarIndicator 0.3s forwards',
},

mobileTimeIndicator: {
borderTop: '1px solid #f0f0f0',
padding: '8px 0',
display: 'flex',
justifyContent: 'center',
},

mobileTime: {
fontSize: '14px',
fontWeight: '600',
color: '#e6376e',
},
mobileProfileButton: {
  background: '#ffffff',
  border: 'none',
  color: '#4B56D2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '8px 16px',
  borderRadius: '24px',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  fontWeight: '500',
  fontSize: '14px',
},
// Mobile Home Screen
mobileHomeContainer: {
display: 'flex',
flexDirection: 'column',
gap: '20px',
},

mobileHomeHeader: {
borderRadius: '16px',
padding: '20px',
backgroundColor: '#fff',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
marginBottom: '8px',
},

mobileHomeHeaderTop: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
marginBottom: '12px',
},

mobileHomeTitle: {
fontSize: '20px',
fontWeight: '700',
color: '#1a2035',
margin: 0,
},

mobileDateTime: {
display: 'flex',
flexDirection: 'column',
alignItems: 'flex-end',
gap: '4px',
},

mobileDate: {
fontSize: '12px',
color: '#6e7a90',
},

mobileWeather: {
display: 'flex',
alignItems: 'center',
gap: '4px',
fontSize: '12px',
color: '#6e7a90',
},

mobileWeatherIcon: {
color: '#e6376e',
},

mobileHomeSubtitle: {
fontSize: '14px',
color: '#6e7a90',
margin: '0 0 16px 0',
},

mobileSearchBar: {
position: 'relative',
width: '100%',
},

mobileSearchIcon: {
position: 'absolute',
left: '12px',
top: '50%',
transform: 'translateY(-50%)',
color: '#6e7a90',
},

mobileSearchInput: {
width: '100%',
padding: '12px 16px 12px 36px',
borderRadius: '12px',
border: '1px solid #e5e7eb',
backgroundColor: '#f9fafb',
fontSize: '14px',
color: '#1a2035',
outline: 'none',
},

// Mobile Stat Cards
mobileStatCards: {
display: 'flex',
flexDirection: 'column',
gap: '12px',
},

mobileStatCard: {
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
},

mobileStatIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '48px',
height: '48px',
borderRadius: '12px',
backgroundColor: 'rgba(230, 55, 110, 0.1)',
color: '#e6376e',
},

mobileStatInfo: {
flex: 1,
marginLeft: '16px',
},

mobileStatValue: {
fontSize: '24px',
fontWeight: '700',
color: '#1a2035',
margin: 0,
},

mobileStatTitle: {
fontSize: '14px',
color: '#6e7a90',
margin: '4px 0 0 0',
},

mobileStatGrowth: {
fontSize: '14px',
fontWeight: '600',
padding: '4px 8px',
borderRadius: '6px',
backgroundColor: 'rgba(16, 185, 129, 0.1)',
},

// Mobile Section Containers
mobileSection: {
marginBottom: '20px',
},

mobileSectionTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#1a2035',
margin: '0 0 12px 0',
},

// Mobile Quick Actions
mobileQuickActions: {
display: 'grid',
gridTemplateColumns: 'repeat(2, 1fr)',
gap: '12px',
},

mobileQuickAction: {
backgroundColor: '#ffffff',
border: 'none',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
gap: '12px',
cursor: 'pointer',
textAlign: 'center',
},

mobileQuickActionIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '48px',
height: '48px',
borderRadius: '12px',
},

mobileQuickActionLabel: {
fontSize: '14px',
fontWeight: '500',
color: '#1a2035',
},

// Mobile Activity List
mobileActivityList: {
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '12px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
display: 'flex',
flexDirection: 'column',
},

mobileActivityItem: {
display: 'flex',
alignItems: 'flex-start',
gap: '12px',
padding: '12px',
borderBottom: '1px solid #f3f4f6',
},

mobileActivityAvatar: {
width: '40px',
height: '40px',
borderRadius: '12px',
overflow: 'hidden',
flexShrink: 0, // Fix the property name here
},

// Create a new separate object for the image styling
mobileActivityAvatarImage: {
width: '100%',
height: '100%',
objectFit: 'cover',
},

mobileActivityContent: {
flex: 1,
},

mobileActivityText: {
fontSize: '14px',
color: '#1a2035',
margin: '0 0 4px 0',
lineHeight: 1.5,
},

mobileActivityUser: {
fontWeight: '600',
color: '#1a2035',
},

mobileActivityAction: {
color: '#6e7a90',
},

mobileActivityTarget: {
fontWeight: '500',
color: '#1a2035',
},

mobileActivityTime: {
fontSize: '12px',
color: '#6e7a90',
},

mobileViewAllButton: {
width: '100%',
marginTop: '12px',
padding: '12px 0',
border: '1px solid #e6376e',
borderRadius: '12px',
backgroundColor: 'transparent',
color: '#e6376e',
fontSize: '14px',
fontWeight: '500',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
},

// Mobile Section Container
mobileSectionContainer: {
display: 'flex',
flexDirection: 'column',
gap: '16px',
},

mobileSectionHeader: {
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

mobileSectionTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#1a2035',
margin: '0 0 16px 0',
},

mobileSearchContainer: {
position: 'relative',
},

mobileSearchInput: {
width: '100%',
padding: '12px 40px 12px 16px',
borderRadius: '12px',
border: '1px solid #e5e7eb',
fontSize: '14px',
backgroundColor: '#f9fafb',
outline: 'none',
},

mobileSearchIcon: {
position: 'absolute',
right: '16px',
top: '50%',
transform: 'translateY(-50%)',
color: '#6e7a90',
},

mobileFilters: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
gap: '12px',
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

mobileFilterSelect: {
flex: 1,
padding: '12px 16px',
borderRadius: '12px',
border: '1px solid #e5e7eb',
backgroundColor: '#f9fafb',
fontSize: '14px',
outline: 'none',
color: '#1a2035',
appearance: 'none',
backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236e7a90\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
backgroundRepeat: 'no-repeat',
backgroundPosition: 'right 16px center',
backgroundSize: '16px',
},

mobileViewToggle: {
display: 'flex',
alignItems: 'center',
borderRadius: '10px',
overflow: 'hidden',
border: '1px solid #e5e7eb',
},

mobileViewToggleButton: {
width: '40px',
height: '40px',
border: 'none',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
backgroundColor: 'transparent',
},

// Mobile User Grid
mobileUserContainer: {
padding: '12px 0',
},

mobileUserGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(1, 1fr)',
gap: '16px',
},

mobileUserCard: {
backgroundColor: '#ffffff',
borderRadius: '16px',
overflow: 'hidden',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

mobileUserCardHeader: {
padding: '16px',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
},

mobileUserInfo: {
display: 'flex',
alignItems: 'flex-start',
gap: '12px',
},

mobileUserAvatar: {
width: '50px',
height: '50px',
borderRadius: '12px',
objectFit: 'cover',
boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
},

mobileUserDetails: {
display: 'flex',
flexDirection: 'column',
gap: '4px',
},

mobileUserName: {
fontSize: '16px',
fontWeight: '600',
color: '#1a2035',
margin: 0,
},

mobileUserEmail: {
fontSize: '13px',
color: '#6e7a90',
margin: 0,
},

mobileStatusBadge: {
padding: '6px 10px',
borderRadius: '10px',
fontSize: '12px',
fontWeight: '600',
color: '#ffffff',
textTransform: 'uppercase',
letterSpacing: '0.5px',
},

mobileUserActions: {
padding: '12px 16px',
display: 'flex',
gap: '10px',
borderTop: '1px solid #f3f4f6',
},

mobileActionButton: {
flex: 1,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '8px',
padding: '10px 0',
borderRadius: '10px',
border: 'none',
backgroundColor: '#616161',
color: '#ffffff',
fontSize: '13px',
fontWeight: '500',
cursor: 'pointer',
},

mobileButtonIcon: {
flexShrink: 0,
},

// Mobile User List View
mobileUserList: {
display: 'flex',
flexDirection: 'column',
gap: '12px',
},

mobileUserListItem: {
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
cursor: 'pointer',
},

mobileUserListInfo: {
display: 'flex',
alignItems: 'center',
gap: '12px',
},

mobileUserListAvatar: {
width: '40px',
height: '40px',
borderRadius: '10px',
objectFit: 'cover',
},

mobileUserListDetails: {
display: 'flex',
flexDirection: 'column',
gap: '2px',
},

mobileUserListName: {
fontSize: '15px',
fontWeight: '600',
color: '#1a2035',
margin: 0,
},

mobileUserListEmail: {
fontSize: '12px',
color: '#6e7a90',
margin: 0,
},

mobileStatusBadgeSmall: {
padding: '4px 8px',
borderRadius: '6px',
fontSize: '11px',
fontWeight: '600',
color: '#ffffff',
textTransform: 'uppercase',
},

// Mobile History View
mobileHistoryList: {
display: 'flex',
flexDirection: 'column',
gap: '16px',
},

mobileHistoryGroup: {
backgroundColor: '#ffffff',
borderRadius: '16px',
overflow: 'hidden',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

mobileHistoryGroupHeader: {
padding: '16px',
backgroundColor: '#1a2035',
color: '#ffffff',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
cursor: 'pointer',
},

mobileHistoryDate: {
fontSize: '16px',
fontWeight: '600',
margin: 0,
},

mobileToggleButton: {
background: 'transparent',
border: 'none',
color: '#ffffff',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
},

mobileHistoryContent: {
transition: 'max-height 0.4s cubic-bezier(0, 1, 0, 1)',
overflow: 'hidden',
},

mobileHistoryItem: {
padding: '16px',
borderBottom: '1px solid #f3f4f6',
},

mobileHistoryItemHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '8px',
},

mobileHistoryTime: {
fontSize: '12px',
color: '#6e7a90',
},

mobileHistoryStatus: {
display: 'inline-block',
padding: '4px 8px',
borderRadius: '6px',
fontSize: '10px',
fontWeight: '600',
color: '#ffffff',
textTransform: 'uppercase',
},

mobileHistoryItemBody: {
marginTop: '8px',
},

mobileHistoryText: {
fontSize: '13px',
color: '#1a2035',
margin: 0,
lineHeight: '1.5',
},

// Mobile User Verification
mobileSettingsContainer: {
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '16px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

mobileSettingsDescription: {
fontSize: '14px',
color: '#6e7a90',
margin: '0 0 16px 0',
lineHeight: '1.5',
},

mobileBulkTextarea: {
width: '100%',
height: '120px',
padding: '12px 16px',
borderRadius: '10px',
border: '1px solid #e5e7eb',
fontSize: '14px',
backgroundColor: '#f9fafb',
resize: 'none',
outline: 'none',
marginBottom: '16px',
fontFamily: 'inherit',
},

mobileCheckButton: {
width: '100%',
padding: '12px 16px',
borderRadius: '10px',
border: 'none',
backgroundColor: '#e6376e',
color: '#ffffff',
fontSize: '14px',
fontWeight: '500',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '8px',
},

mobileMissingUsers: {
marginTop: '20px',
backgroundColor: '#f9fafb',
borderRadius: '10px',
padding: '16px',
},

mobileMissingTitle: {
fontSize: '16px',
fontWeight: '600',
color: '#ef4444',
margin: '0 0 12px 0',
},

mobileMissingList: {
margin: 0,
padding: '0 0 0 20px',
},

mobileMissingItem: {
fontSize: '14px',
color: '#1a2035',
margin: '8px 0',
},

// Mobile Modals
mobileModalContainer: {
backgroundColor: '#ffffff',
borderRadius: '16px',
overflow: 'hidden',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
},

// Mobile Notifications
mobileNotificationsContainer: {
position: 'absolute',
top: '70px',
left: '16px',
right: '16px',
zIndex: 100,
borderRadius: '16px',
boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
backgroundColor: '#ffffff',
overflow: 'hidden',
},
mobileMenuOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1000,
  backdropFilter: 'blur(5px)',
  touchAction: 'none', // Verhindert Scrollen unter dem Modal
},

mobileMenu: {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '90vh',
  backgroundColor: '#ffffff',
  borderTopLeftRadius: '20px',
  borderTopRightRadius: '20px',
  boxShadow: '0 -5px 25px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
},

mobileMenuHandle: {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: '12px 0 8px',
},

mobileMenuHandleBar: {
  width: '50px',
  height: '5px',
  backgroundColor: '#e0e0e0',
  borderRadius: '10px',
},

mobileMenuHeader: {
  padding: '10px 20px 20px',
  borderBottom: '1px solid #f0f0f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
},

mobileMenuProfile: {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
},

mobileMenuProfileImage: {
  width: '48px',
  height: '48px',
  borderRadius: '52px',
  objectFit: 'cover',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
},

mobileMenuProfileInfo: {
  display: 'flex',
  flexDirection: 'column',
},

mobileMenuProfileName: {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 4px 0',
},

mobileMenuProfileRole: {
  fontSize: '12px',
  color: '#6e7a90',
  margin: 0,
},

mobileMenuCloseButton: {
  padding: '8px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  color: '#1a2035',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
},

mobileMenuContent: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '5px 20px 20px',
  maxHeight: 'calc(90vh - 80px)',
  overflowY: 'auto',
},

mobileMenuNavigation: {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
},

mobileMenuNavButton: {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '14px 16px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  transition: 'all 0.2s ease',
},

mobileMenuNavLabel: {
  fontSize: '15px',
  fontWeight: '500',
  color: '#1a2035',
},

mobileMenuFooter: {
  marginTop: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
},

mobileMenuDivider: {
  height: '1px',
  backgroundColor: '#f0f0f0',
  margin: '8px 0',
},

mobileMenuSettings: {
  display: 'flex',
  gap: '12px',
  marginBottom: '16px',
},

mobileMenuSettingButton: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '12px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: '#f5f7fb',
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
},

mobileMenuLogoutButton: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '16px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: '#fee2e2',
  color: '#ef4444',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: '500',
  boxShadow: '0 1px 3px rgba(239, 68, 68, 0.2)',
},
// Login styles
loginBody: {
minHeight: '100vh',
backgroundColor: '#f5f7fb',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
position: 'relative',
overflow: 'hidden',
padding: '20px',
},

loginBackground: {
position: 'absolute',
top: 0,
left: 0,
width: '100%',
height: '100%',
zIndex: -1,
},

loginBackgroundOverlay: {
position: 'absolute',
top: 0,
left: 0,
width: '100%',
height: '100%',
backgroundColor: 'rgba(0, 0, 0, 0.03)',
zIndex: 1,
},

loginBackgroundGradient: {
position: 'absolute',
top: 0,
left: 0,
width: '100%',
height: '100%',
background: 'linear-gradient(to bottom right, #e6376e10, #3b82f610)',
zIndex: 2,
},

loginBackgroundPattern: {
position: 'absolute',
top: 0,
left: 0,
width: '100%',
height: '100%',
backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px)',
backgroundSize: '20px 20px',
zIndex: 3,
},

loginContainer: {
width: '100%',
backgroundColor: '#ffffff',
borderRadius: '20px',
overflow: 'hidden',
boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
display: 'flex',
flexDirection: 'column',
},

loginLeft: {
display: 'none',
},

loginRight: {
padding: '40px 20px',
backgroundColor: '#ffffff',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
},

loginRightFooter: {
marginTop: '30px',
textAlign: 'center',
},

loginCopyright: {
fontSize: '12px',
color: '#6e7a90',
margin: 0,
},

loginCard: {
width: '100%',
border: 'none',
boxShadow: 'none',
padding: 0,
},

loginHeader: {
marginBottom: '30px',
textAlign: 'center',
},

loginLogoMobile: {
height: '30px',
margin: '0 auto 20px',
display: 'block',
},

loginTitle: {
fontSize: '22px',
fontWeight: '700',
color: '#1a2035',
marginBottom: '8px',
},

loginSubtitle: {
fontSize: '14px',
color: '#6e7a90',
margin: 0,
},

loginForm: {
display: 'flex',
flexDirection: 'column',
gap: '20px',
},

formGroup: {
display: 'flex',
flexDirection: 'column',
gap: '8px',
},

inputLabel: {
fontSize: '14px',
fontWeight: '500',
color: '#1a2035',
},

inputWrapper: {
position: 'relative',
borderRadius: '12px',
border: '1px solid #e5e7eb',
overflow: 'hidden',
transition: 'box-shadow 0.2s ease',
},

inputIcon: {
position: 'absolute',
left: '16px',
top: '50%',
transform: 'translateY(-50%)',
color: '#6e7a90',
},

loginInput: {
width: '100%',
padding: '14px 16px 14px 45px',
border: 'none',
fontSize: '15px',
backgroundColor: '#f9fafb',
outline: 'none',
},

loginButton: {
padding: '16px',
borderRadius: '12px',
border: 'none',
backgroundColor: '#e6376e',
color: '#ffffff',
fontSize: '16px',
fontWeight: '600',
cursor: 'pointer',
position: 'relative',
overflow: 'hidden',
},

loginButtonProgress: {
position: 'absolute',
left: 0,
top: 0,
bottom: 0,
width: 0,
backgroundColor: 'rgba(255, 255, 255, 0.2)',
animation: 'progress 2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
},

loadingSpinnerContainer: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '8px',
},

loadingSpinner: {
animation: 'spin 1s linear infinite',
},

loginNote: {
fontSize: '12px',
color: '#6e7a90',
margin: '20px 0 0 0',
lineHeight: '1.5',
textAlign: 'center',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
gap: '8px',
},

loginLink: {
color: '#e6376e',
textDecoration: 'none',
fontWeight: '500',
},

errorAlert: {
display: 'flex',
alignItems: 'center',
gap: '12px',
padding: '14px 16px',
borderRadius: '12px',
backgroundColor: '#fee2e2',
color: '#ef4444',
marginBottom: '20px',
fontSize: '14px',
},

errorIcon: {
flexShrink: 0,
},

// Success toast
successToast: {
position: 'fixed',
bottom: '20px',
left: '20px',
right: '20px',
backgroundColor: '#dcfce7',
borderRadius: '12px',
boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
overflow: 'hidden',
zIndex: 1000,
},

successToastContent: {
display: 'flex',
alignItems: 'center',
gap: '12px',
padding: '16px',
},

successToastIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '24px',
height: '24px',
borderRadius: '50%',
backgroundColor: '#10b981',
color: '#ffffff',
flexShrink: 0,
},

successToastProgress: {
height: '4px',
backgroundColor: '#10b981',
animation: 'progress 3s linear forwards',
},

// Welcome modal
welcomeModalOverlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.5)',
zIndex: 1100,
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
padding: '20px',
backdropFilter: 'blur(5px)',
},

welcomeModal: {
width: '100%',
maxWidth: '550px',
backgroundColor: '#ffffff',
borderRadius: '16px',
overflow: 'hidden',
boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
},

welcomeModalHeader: {
padding: '20px',
borderBottom: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
},

welcomeModalLogo: {
display: 'flex',
alignItems: 'center',
gap: '12px',
},

welcomeLogoImage: {
height: '24px',
},

welcomeLogoText: {
fontSize: '18px',
fontWeight: '600',
color: '#1a2035',
margin: 0,
},

welcomeModalClose: {
padding: '8px',
borderRadius: '10px',
border: 'none',
backgroundColor: 'rgba(0, 0, 0, 0.05)',
color: '#1a2035',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
},

welcomeModalContent: {
padding: '30px 20px',
},

welcomeModalTitle: {
fontSize: '24px',
fontWeight: '700',
color: '#1a2035',
margin: '0 0 16px 0',
textAlign: 'center',
},

welcomeModalText: {
fontSize: '16px',
color: '#6e7a90',
margin: '0 0 30px 0',
lineHeight: '1.6',
textAlign: 'center',
},

welcomeFeatures: {
display: 'flex',
flexDirection: 'column',
gap: '20px',
},

welcomeFeature: {
display: 'flex',
gap: '16px',
padding: '16px',
borderRadius: '12px',
backgroundColor: '#f9fafb',
},

welcomeFeatureIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '40px',
height: '40px',
borderRadius: '10px',
backgroundColor: 'rgba(230, 55, 110, 0.1)',
flexShrink: 0,
},

welcomeFeatureText: {
flex: 1,
},

welcomeFeatureTitle: {
fontSize: '16px',
fontWeight: '600',
color: '#1a2035',
margin: '0 0 4px 0',
},

welcomeFeatureDescription: {
fontSize: '14px',
color: '#6e7a90',
margin: 0,
lineHeight: '1.5',
},

welcomeModalFooter: {
padding: '20px',
borderTop: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'center',
},

welcomeModalButton: {
padding: '12px 24px',
borderRadius: '12px',
border: 'none',
backgroundColor: '#e6376e',
color: '#ffffff',
fontSize: '16px',
fontWeight: '600',
cursor: 'pointer',
},

// Page transition overlay
pageTransitionOverlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(255, 255, 255, 0.5)',
backdropFilter: 'blur(5px)',
zIndex: 900,
},
};

// Dark mode mobile styles
const darkMobileStyles = {
dashboardContainer: {
backgroundColor: '#121825',
},

mobileTopBar: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileMenuButton: {
backgroundColor: 'rgba(230, 55, 110, 0.2)',
},

mobileNotificationButton: {
backgroundColor: 'rgba(230, 55, 110, 0.2)',
},

mobileHomeHeader: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileHomeTitle: {
color: '#ffffff',
},

mobileHomeSubtitle: {
color: '#9ca3af',
},

mobileSearchInput: {
backgroundColor: '#2a3349',
borderColor: '#2a3349',
color: '#ffffff',
},

mobileSearchIcon: {
color: '#9ca3af',
},

mobileStatCard: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileStatValue: {
color: '#ffffff',
},

mobileStatTitle: {
color: '#9ca3af',
},

mobileSectionTitle: {
color: '#ffffff',
},

mobileQuickAction: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileQuickActionLabel: {
color: '#ffffff',
},

mobileActivityList: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileActivityItem: {
borderBottom: '1px solid #2a3349',
},

mobileActivityText: {
color: '#f9fafb',
},

mobileActivityUser: {
color: '#ffffff',
},

mobileActivityAction: {
color: '#9ca3af',
},

mobileActivityTarget: {
color: '#ffffff',
},

mobileActivityTime: {
color: '#9ca3af',
},

mobileSectionHeader: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileSectionTitle: {
color: '#ffffff',
},

mobileFilters: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileFilterSelect: {
backgroundColor: '#2a3349',
borderColor: '#2a3349',
color: '#ffffff',
},

mobileViewToggle: {
borderColor: '#2a3349',
backgroundColor: '#2a3349',
},

mobileViewToggleButton: {
color: '#9ca3af',
},

mobileUserCard: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileUserName: {
color: '#ffffff',
},

mobileUserEmail: {
color: '#9ca3af',
},

mobileUserActions: {
borderTop: '1px solid #2a3349',
},

mobileUserListItem: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileUserListName: {
color: '#ffffff',
},

mobileUserListEmail: {
color: '#9ca3af',
},

mobileHistoryGroup: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileHistoryGroupHeader: {
backgroundColor: '#2a3349',
},

mobileHistoryItem: {
borderBottom: '1px solid #2a3349',
},

mobileHistoryText: {
color: '#f9fafb',
},

mobileHistoryTime: {
color: '#9ca3af',
},

mobileSettingsContainer: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileSettingsDescription: {
color: '#9ca3af',
},

mobileBulkTextarea: {
backgroundColor: '#2a3349',
borderColor: '#2a3349',
color: '#ffffff',
},

mobileMissingUsers: {
backgroundColor: '#2a3349',
},

mobileMissingTitle: {
color: '#ef4444',
},

mobileMissingItem: {
color: '#f9fafb',
},

mobileModalContainer: {
backgroundColor: '#1a2035',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
},

mobileNotificationsContainer: {
backgroundColor: '#1a2035',
boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
},

mobileMenu: {
backgroundColor: '#1a2035',
boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
},

mobileMenuHeader: {
borderBottom: '1px solid #2a3349',
},

mobileMenuProfileName: {
color: '#ffffff',
},

mobileMenuProfileRole: {
color: '#9ca3af',
},

mobileMenuCloseButton: {
backgroundColor: 'rgba(255, 255, 255, 0.1)',
color: '#ffffff',
},

mobileMenuNavButton: {
color: '#f9fafb',
},

mobileMenuNavLabel: {
color: '#f9fafb',
},

mobileMenuDivider: {
backgroundColor: '#2a3349',
},

mobileMenuSettingButton: {
backgroundColor: '#2a3349',
color: '#f9fafb',
},

// Bottom Navigation for dark mode
mobileBottomNavContainer: {
backgroundColor: '#1a2035',
boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
borderTop: '1px solid #2a3349',
},

mobileBottomNavButton: {
color: '#9ca3af',
},

mobileTimeIndicator: {
borderTop: '1px solid #2a3349',
},

mobileTime: {
color: '#e6376e',
},

// Login styles for dark mode
loginBody: {
backgroundColor: '#121825',
},

loginBackgroundOverlay: {
backgroundColor: 'rgba(0, 0, 0, 0.1)',
},

loginBackgroundGradient: {
background: 'linear-gradient(to bottom right, #e6376e20, #3b82f620)',
},

loginContainer: {
backgroundColor: '#1a2035',
},

loginRight: {
backgroundColor: '#1a2035',
},

loginTitle: {
color: '#ffffff',
},

loginSubtitle: {
color: '#9ca3af',
},

inputLabel: {
color: '#f9fafb',
},

inputWrapper: {
borderColor: '#2a3349',
},

inputIcon: {
color: '#9ca3af',
},

loginInput: {
backgroundColor: '#2a3349',
color: '#ffffff',
},

loginNote: {
color: '#9ca3af',
},

loginCopyright: {
color: '#9ca3af',
},

// Welcome modal for dark mode
welcomeModal: {
backgroundColor: '#1a2035',
},

welcomeModalHeader: {
borderBottom: '1px solid #2a3349',
},

welcomeLogoText: {
color: '#ffffff',
},

welcomeModalClose: {
backgroundColor: 'rgba(255, 255, 255, 0.1)',
color: '#ffffff',
},

welcomeModalTitle: {
color: '#ffffff',
},

welcomeModalText: {
color: '#9ca3af',
},

welcomeFeature: {
backgroundColor: '#2a3349',
},

welcomeFeatureTitle: {
color: '#ffffff',
},

welcomeFeatureDescription: {
color: '#9ca3af',
},

welcomeModalFooter: {
borderTop: '1px solid #2a3349',
},
};

// Desktop styles
const desktopStyles = {
// CSS Variables
':root': {
'--accent-color': '#e6376e',
'--accent-color-light': '#e6376e33',
'--accent-color-hover': '#d32e60',
},

// Main container styles
dashboardContainer: {
minHeight: '100vh',

backgroundColor: '#f5f7fb',
display: 'flex',
position: 'relative',
fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
overflow: 'hidden',
},

// Toast
successToast: {
position: 'fixed',
bottom: '30px',
right: '30px',
backgroundColor: '#dcfce7',
borderRadius: '12px',
boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
zIndex: 1000,
overflow: 'hidden',
maxWidth: '350px',
width: '100%',
},

successToastContent: {
display: 'flex',
alignItems: 'center',
gap: '12px',
padding: '16px',
},

successToastIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '24px',
height: '24px',
borderRadius: '50%',
backgroundColor: '#10b981',
color: '#ffffff',
flexShrink: 0,
},

successToastProgress: {
height: '4px',
backgroundColor: '#10b981',
animation: 'progress 3s linear forwards',
},

// Sidebar
sidebar: {
position: 'fixed',
top: 0,
left: 0,
bottom: 0,
backgroundColor: '#000',
background: 'linear-gradient(90deg, #3d3935 90%,rgb(255, 255, 255) 100%)',
borderRight: '1px solid rgb(245, 247, 251)',    zIndex: 50,
display: 'flex',
flexDirection: 'column',
width: '280px',
transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
// boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
overflow: 'hidden',
},

sidebarHeader: {
padding: '24px 20px',
display: 'flex',
alignItems: 'center',
gap: '15px',
borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
},

brandContainer: {
display: 'flex',
alignItems: 'center',
gap: '12px',
},

brandLogo: {
height: '24px',
filter: 'brightness(0) invert(1)',
},

sidebarTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#ffffff',
margin: 0,
whiteSpace: 'nowrap',
opacity: 1,
transition: 'opacity 0.2s ease',
},

collapseButton: {
width: '38px',
height: '38px',
borderRadius: '10px',
border: 'none',
backgroundColor: 'rgba(255, 255, 255, 0.1)',
cursor: 'pointer',
color: '#ffffff',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
transition: 'background-color 0.2s ease',
},

sidebarDivider: {
height: '1px',
backgroundColor: 'rgba(255, 255, 255, 0.1)',
margin: '8px 20px',
},

// Profile section
profileSection: {
padding: '24px 20px',
display: 'flex',
alignItems: 'center',
gap: '15px',
borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
marginLeft:-8,

},

profileImageContainer: {
position: 'relative',
width: '50px',
height: '50px',
cursor: 'pointer',
},

profilePicture: {
width: '50px',
height: '50px',
borderRadius: '54px',
objectFit: 'cover',
border: '2px solid rgba(255, 255, 255, 0.2)',
transition: 'border-color 0.2s ease, transform 0.2s ease',
},

editProfileButton: {
position: 'absolute',
bottom: '-6px',
right: '-6px',
width: '22px',
height: '22px',
borderRadius: '50px',
backgroundColor: '#000',
border: '2px solid #1a2035',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
padding: 0,
boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
transition: 'background-color 0.2s ease, transform 0.2s ease',
},

profileInfo: {
display: 'flex',
flexDirection: 'column',
overflow: 'hidden',
},

profileName: {
fontSize: '16px',
fontWeight: '600',
color: '#ffffff',
margin: 0,
marginBottom: '4px',
whiteSpace: 'nowrap',
textOverflow: 'ellipsis',
overflow: 'hidden',
},

profileRole: {
fontSize: '13px',
color: 'rgba(255, 255, 255, 0.6)',
margin: 0,
},

// Navigation
navigation: {
padding: '20px',
display: 'flex',
flexDirection: 'column',
gap: '24px',
marginLeft:-8,

flex: 1,
overflowY: 'auto',
scrollbarWidth: 'thin',
scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
},

navSection: {
display: 'flex',
flexDirection: 'column',
gap: '8px',
},

navSectionTitle: {
fontSize: '11px',
fontWeight: '700',
color: 'rgba(255, 255, 255, 0.4)',
textTransform: 'uppercase',
letterSpacing: '1.5px',
margin: '0 0 8px 12px',
},

navButton: {
display: 'flex',
alignItems: 'center',
gap: '14px',
padding: '14px 16px',
borderRadius: '14px',
border: 'none',
backgroundColor: 'transparent',
cursor: 'pointer',
width: '200%',
transition: 'all 0.2s ease',
color: 'rgba(255, 0, 179, 0.7)',
textAlign: 'left',
position: 'relative',
overflow: 'hidden',
},

navIconContainer: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '24px',
height: '24px',
flexShrink: 0,
},

navLabel: {
fontSize: '15px',
fontWeight: '500',
whiteSpace: 'nowrap',
opacity: 1,
transition: 'opacity 0.2s ease',
},

// Sidebar footer
sidebarFooter: {
padding: '20px',
borderTop: '1px solid rgba(255, 255, 255, 0.1)',
},

logoutButton: {
display: 'flex',
alignItems: 'center',
gap: '14px',
padding: '14px 16px',
borderRadius: '14px',
width: '90%',
border: 'none',
backgroundColor: 'rgb(255, 255, 255)',
color: '#ef4444',
cursor: 'pointer',
fontSize: '15px',
fontWeight: '500',
transition: 'background-color 0.2s ease',
},

// Color picker
colorPickerContainer: {
position: 'absolute',
bottom: '100px',
left: '20px',
right: '20px',
zIndex: 100,
},

colorPicker: {
backgroundColor: '#ffffff',
borderRadius: '14px',
boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
overflow: 'hidden',
},

colorPickerHeader: {
padding: '16px',
borderBottom: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
},

colorPickerTitle: {
fontSize: '16px',
fontWeight: '600',
color: '#1a2035',
margin: 0,
},

colorPickerClose: {
padding: '6px',
borderRadius: '8px',
border: 'none',
backgroundColor: 'rgba(0, 0, 0, 0.05)',
color: '#6e7a90',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
},

colorPickerContent: {
padding: '16px',
},

colorPickerSwatches: {
display: 'grid',
gridTemplateColumns: 'repeat(5, 1fr)',
gap: '10px',
},

colorPickerSwatch: {
width: '30px',
height: '30px',
borderRadius: '8px',
border: 'none',
cursor: 'pointer',
},

// Main content
mainContent: {
flex: 1,
padding: '30px',
paddingTop: '20px',
// background: 'linear-gradient(270deg, rgb(0, 0, 0) 0%,#fff 4%)',

backgroundColor: '#f5f7fb',
marginLeft: '280px',
transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
minHeight: '100vh',
overflowX: 'hidden',

},

// Top bar
topBar: {
display: 'flex',
background: 'linear-gradient(210deg,rgb(0, 0, 0) 0%,rgb(245, 247, 251)12%)',

justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '30px',
backgroundColor: '#ffffff',
borderRadius: '16px',
padding: '20px 24px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.65)',
},

pageInfo: {
display: 'flex',
flexDirection: 'column',
gap: '5px',
},

pageTitle: {
fontSize: '24px',
fontWeight: '700',
color: '#1a2035',
margin: 0,
},

pageBreadcrumb: {
fontSize: '14px',
color: '#6e7a90',
margin: 0,
display: 'flex',
alignItems: 'center',
gap: '8px',
},

breadcrumbSeparator: {
color: '#d1d5db',
},

currentPage: {
color: '#e6376e',
fontWeight: '500',
},

topBarActions: {
display: 'flex',
alignItems: 'center',
gap: '16px',
},

searchContainer: {
position: 'relative',
width: '300px',
},

searchIcon: {
position: 'absolute',
left: '16px',
top: '50%',
transform: 'translateY(-50%)',
color: '#6e7a90',
},

searchInput: {
width: '100%',
padding: '14px 16px 14px 45px',
borderRadius: '14px',
border: '1px solid #e5e7eb',
fontSize: '14px',
backgroundColor: '#f9fafb',
boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
transition: 'all 0.2s ease',
outline: 'none',
},

actionsContainer: {
display: 'flex',
alignItems: 'center',
gap: '16px',
},

notificationContainer: {
position: 'relative',
},

notificationButton: {
padding: '12px',
borderRadius: '14px',
border: 'none',
backgroundColor: '#ffffff',
cursor: 'pointer',
color: '#1a2035',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
transition: 'all 0.2s ease',
position: 'relative',
},

notificationBadge: {
position: 'absolute',
top: '-5px',
right: '-5px',
width: '20px',
height: '20px',
borderRadius: '6px',
backgroundColor: '#e6376e',
color: '#ffffff',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontSize: '12px',
fontWeight: '600',
boxShadow: '0 2px 5px rgba(230, 55, 110, 0.3)',
},

notificationsContainer: {
position: 'absolute',
top: '50px',
right: '0',
width: '360px',
backgroundColor: '#ffffff',
borderRadius: '16px',
boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
zIndex: 100,
overflow: 'hidden',
},

profileDropdown: {
display: 'flex',
alignItems: 'center',
gap: '12px',
padding: '8px 8px 8px 8px',
borderRadius: '14px',
border: 'none',
backgroundColor: '#ffffff',
cursor: 'pointer',
boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
transition: 'all 0.2s ease',
},

profileDropdownImage: {
width: '38px',
height: '38px',
borderRadius: '10px',
objectFit: 'cover',
},

profileDropdownName: {
fontSize: '14px',
fontWeight: '500',
color: '#1a2035',
},

// Content wrapper
contentWrapper: {
display: 'flex',
flexDirection: 'column',
gap: '30px',
},

// Dashboard Grid
dashboardGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(4, 1fr)',

gridTemplateRows: 'auto auto auto',
gap: '24px',
gridTemplateAreas: `
"welcome welcome welcome welcome"
"stat1 stat2 stat3 stat4"
"activity activity chart chart"
"quickactions quickactions chart chart"
`,
},

// Welcome Card
welcomeCard: {
gridArea: 'welcome',
backgroundColor: '#ffffff',
borderRadius: '20px',
overflow: 'hidden',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
display: 'flex',
height: '200px',
},

welcomeCardContent: {
flex: 1,
padding: '30px',
display: 'flex',
flexDirection: 'column',
justifyContent: 'center',
},

welcomeCardTitle: {
fontSize: '28px',
fontWeight: '700',
color: '#1a2035',
margin: '0 0 10px 0',
},

welcomeCardText: {
fontSize: '16px',
color: '#6e7a90',
margin: '0 0 24px 0',
maxWidth: '500px',
},

welcomeCardActions: {
display: 'flex',
gap: '12px',
},

welcomeCardButton: {
padding: '12px 20px',
borderRadius: '14px',
border: 'none',
backgroundColor: '#e6376e',
color: '#ffffff',
fontSize: '14px',
fontWeight: '500',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
transition: 'all 0.2s ease',
},

welcomeCardOutlineButton: {
padding: '12px 20px',
borderRadius: '14px',
border: '1px solid #e5e7eb',
backgroundColor: 'transparent',
color: '#1a2035',
fontSize: '14px',
fontWeight: '500',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
transition: 'all 0.2s ease',
},

welcomeCardImageContainer: {
width: '40%',
maxWidth: '400px',
position: 'relative',
overflow: 'hidden',
},

welcomeCardImage: {
width: '100%',
height: '100%',
position: 'relative',
},

welcomeCardImageSrc: {
width: '100%',
height: '100%',
objectFit: 'cover',
},

// Stats Row
statsRow: {
gridArea: 'stat1 / stat1 / stat1 / stat4',
display: 'grid',
gridTemplateColumns: 'repeat(4, 1fr)',
gap: '24px',
},

statCard: {
backgroundColor: '#ffffff',
borderRadius: '20px',
padding: '24px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
display: 'flex',
flexDirection: 'column',
position: 'relative',
overflow: 'hidden',
transition: 'transform 0.3s ease, box-shadow 0.3s ease',
},

statCardTop: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
marginBottom: '20px',
},

statIcon: {
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '48px',
height: '48px',
borderRadius: '14px',
},

statGrowth: {
fontSize: '14px',
fontWeight: '600',
padding: '6px 10px',
borderRadius: '8px',
backgroundColor: 'rgba(16, 185, 129, 0.1)',
},

statValue: {
fontSize: '28px',
fontWeight: '700',
color: '#1a2035',
margin: '0 0 5px 0',
},

statTitle: {
fontSize: '14px',
color: '#6e7a90',
margin: 0,
},

statChart: {
position: 'absolute',
bottom: '0',
right: '0',
width: '80px',
height: '40px',
opacity: 0.5,
},

miniChart: {
display: 'flex',
alignItems: 'flex-end',
height: '100%',
gap: '3px',
},

miniChartBar: {
flex: 1,
borderRadius: '2px 2px 0 0',
transition: 'height 0.3s ease',
},

// Recent Activity Card
recentActivityCard: {
gridArea: 'activity',
backgroundColor: '#ffffff',
borderRadius: '20px',
padding: '24px',
boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
display: 'flex',
flexDirection: 'column',
},

recentActivityHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '24px',
},

recentActivityTitle: {
fontSize: '18px',
fontWeight: '600',
color: '#1a2035',
margin: 0,
},

recentActivityViewButton: {
background: 'none',
border: 'none',
color: '#e6376e',
fontSize: '14px',
fontWeight: '500',
cursor: 'pointer',
},

recentActivityList: {
display: 'flex',
flexDirection: 'column',
gap: '16px',
},

recentActivityItem: {
display: 'flex',
alignItems: 'flex-start',
gap: '16px',
padding: '16px',
borderRadius: '14px',
backgroundColor: '#f9fafb',
transition: 'transform 0.2s ease',
},

recentActivityAvatar: {
width: '44px',
height: '44px',
borderRadius: '12px',
overflow: 'hidden',
flexShrink: 0,
},

recentActivityAvatarImage: {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
},

recentActivityContent: {
  flex: 1,
},

recentActivityText: {
  fontSize: '14px',
  color: '#1a2035',
  margin: '0 0 6px 0',
  lineHeight: 1.5,
},

recentActivityUser: {
  fontWeight: '600',
  color: '#1a2035',
},

recentActivityTarget: {
  fontWeight: '500',
  color: '#1a2035',
},

recentActivityTime: {
  fontSize: '12px',
  color: '#6e7a90',
},

// Chart Card
chartCard: {
  gridArea: 'chart',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  padding: '24px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
  display: 'flex',
  flexDirection: 'column',
},

chartCardHeader: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '24px',
},

chartCardTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 5px 0',
},

chartCardSubtitle: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: 0,
},

chartCardSelect: {
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  color: '#1a2035',
  fontSize: '14px',
  outline: 'none',
  cursor: 'pointer',
},

chartContainer: {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
},

chart: {
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  gap: '30px',
},

chartLabels: {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '12px',
},

chartLabel: {
  fontSize: '12px',
  color: '#6e7a90',
  textAlign: 'center',
  flex: 1,
},

chartBars: {
  flex: 1,
  height: '200px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
},

chartBarGroup: {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '6px',
  height: '100%',
  flex: 1,
},

chartBar: {
  flex: 1,
  maxWidth: '15px',
  borderRadius: '4px 4px 0 0',
  transition: 'height 0.5s ease',
},

chartLegend: {
  display: 'flex',
  justifyContent: 'center',
  gap: '24px',
  marginTop: '12px',
},

chartLegendItem: {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  color: '#6e7a90',
},

chartLegendColor: {
  width: '12px',
  height: '12px',
  borderRadius: '3px',
},

// Quick Actions Card
quickActionsCard: {
  gridArea: 'quickactions',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  padding: '24px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
},

quickActionsTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 24px 0',
},

quickActionsList: {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
},

quickActionItem: {
  backgroundColor: '#f9fafb',
  borderRadius: '16px',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer',
},

quickActionIcon: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '14px',
},

quickActionTitle: {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1a2035',
  margin: 0,
},

quickActionDescription: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: 0,
  lineHeight: 1.5,
},

quickActionButton: {
  marginTop: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  color: '#1a2035',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
},

// Content area
contentArea: {
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  padding: '24px',
  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.42)',
},

contentHeader: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
},

headerLeft: {
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
},

contentTitle: {
  fontSize: '20px',
  fontWeight: '600',
  color: '#1a2035',
  margin: 0,
},

contentDescription: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: 0,
},

headerRight: {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
},

viewSwitcher: {
  display: 'flex',
  borderRadius: '10px',
  overflow: 'hidden',
  border: '1px solid #e5e7eb',
},

viewSwitcherButton: {
  width: '40px',
  height: '40px',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
},

filterSelect: {
  padding: '10px 16px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  color: '#1a2035',
  fontSize: '14px',
  outline: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minWidth: '140px',
},

historyDatePicker: {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 16px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
},

historyDatePickerIcon: {
  color: '#6e7a90',
},

historyDatePickerSelect: {
  border: 'none',
  backgroundColor: 'transparent',
  color: '#1a2035',
  fontSize: '14px',
  outline: 'none',
  cursor: 'pointer',
},

// User Grid View
usersGrid: {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
  gap: '24px',
  marginTop: '16px',
},

userCard: {
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
  border: '1px solid #f3f4f6',
  cursor: 'pointer',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
},

userCardHeader: {
  padding: '20px',
  borderBottom: '1px solid #f3f4f6',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
},

userImage: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
},

userAvatar: {
  width: '60px',
  height: '60px',
  borderRadius: '50px',
  objectFit: 'cover',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  border: '2px solid #ffffff',
},

statusBadge: {
  padding: '6px 10px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#ffffff',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
},

userCardBody: {
  padding: '20px',
  textAlign: 'center',
},

userCardName: {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 6px 0',
},

userCardEmail: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: 0,
},

userCardActions: {
  padding: '16px 20px 20px',
  display: 'flex',
  gap: '10px',
},

userCardButton: {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 0',
  borderRadius: '12px',
  border: 'none',
  backgroundColor: '#616161',
  color: '#ffffff',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
},

buttonIcon: {
  flexShrink: 0,
},

// Table
tableContainer: {
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid #f3f4f6',
  marginTop: '16px',
},

table: {
  width: '100%',
  borderCollapse: 'collapse',
},

tableHead: {
  backgroundColor: '#f9fafb',
},

tableHeader: {
  padding: '16px 20px',
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: '600',
  color: '#6e7a90',
  borderBottom: '1px solid #f3f4f6',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
},

headerContent: {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
},

sortIndicator: {
  width: '16px',
  textAlign: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
},

tableRow: {
  borderBottom: '1px solid #f3f4f6',
  backgroundColor: '#ffffff',
  transition: 'background-color 0.2s ease',
  cursor: 'pointer',
},

tableCell: {
  padding: '16px 20px',
  fontSize: '14px',
  color: '#1a2035',
  whiteSpace: 'nowrap',
},

userInfo: {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
},

userName: {
  fontWeight: '500',
  fontSize: '14px',
  color: '#1a2035',
},

actionButtons: {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
},

actionButton: {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 12px',
  borderRadius: '10px',
  border: 'none',
  fontSize: '13px',
  fontWeight: '500',
  color: '#ffffff',
  backgroundColor: '#616161',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
},

// History
historyContainer: {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  marginTop: '16px',
},

dateGroup: {
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
  background: '#ffffff',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
},

dateHeader: {
  padding: '16px 24px',
  backgroundColor: '#e6376e',
  color: '#ffffff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
},

dateTitle: {
  margin: 0,
  fontSize: '16px',
  fontWeight: '600',
},

toggleButton: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: '#ffffff',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '8px',
  transition: 'background-color 0.2s ease',
},

toggleIcon: {
  transition: 'transform 0.3s ease',
},

dateContent: {
  overflow: 'hidden',
  transition: 'max-height 0.4s cubic-bezier(0, 1, 0, 1)',
},

historyTable: {
  width: '100%',
  borderCollapse: 'collapse',
},

historyTableHead: {
  backgroundColor: '#f9fafb',
},

historyTh: {
  padding: '14px 20px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: '600',
  color: '#6e7a90',
  borderBottom: '1px solid #f3f4f6',
  whiteSpace: 'nowrap',
},

historyRow: {
  transition: 'background-color 0.2s ease',
  cursor: 'default',
},

historyTd: {
  padding: '14px 20px',
  fontSize: '14px',
  color: '#1a2035',
  borderBottom: '1px solid #f3f4f6',
  whiteSpace: 'nowrap',
},

historyStatus: {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: '600',
  color: '#ffffff',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
},

// User Verification
settingsContainer: {
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
},

settingsSection: {
  borderRadius: '56px',
  padding: '24px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
},

settingsTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 10px 0',
},

settingsDescription: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: '0 0 20px 0',
  lineHeight: '1.5',
},

bulkTextarea: {
  width: '100%',
  height: '150px',
  padding: '16px',
  borderRadius: '14px',
  border: '1px solid #e5e7eb',
  fontSize: '14px',
  backgroundColor: '#ffffff',
  resize: 'vertical',
  outline: 'none',
  marginBottom: '20px',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
},

verifyButton: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '14px 24px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: '#e6376e',
  color: '#ffffff',
  fontWeight: '500',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  width: 'fit-content',
},

resultSection: {
  borderRadius: '16px',
  padding: '24px',
  backgroundColor: '#fee2e2',
  border: '1px solid #fecaca',
},

resultTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#ef4444',
  margin: '0 0 10px 0',
},

resultDescription: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: '0 0 20px 0',
  lineHeight: '1.5',
},

missingUsers: {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
},

missingUser: {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '10px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fee2e2',
},

missingUserIcon: {
  color: '#ef4444',
},

missingUserName: {
  fontSize: '14px',
  color: '#ef4444',
  fontWeight: '500',
},

// Modals
modalOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(5px)',
},

modalContainer: {
  width: '100%',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  backgroundColor: '#ffffff',
},

// Password modal
passwordModal: {
  width: '450px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
},

modalHeader: {
  padding: '24px',
  backgroundColor: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
},

modalTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1a2035',
  margin: 0,
},

modalCloseButton: {
  padding: '8px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  cursor: 'pointer',
  color: '#6e7a90',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
},

modalBody: {
  padding: '24px',
},

passwordError: {
  backgroundColor: '#fee2e2',
  color: '#ef4444',
  padding: '14px 16px',
  borderRadius: '12px',
  fontSize: '14px',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
},

passwordForm: {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
},

passwordFormGroup: {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
},

passwordLabel: {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1a2035',
},

passwordInput: {
  padding: '14px 16px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  fontSize: '14px',
  backgroundColor: '#f9fafb',
  width: '100%',
  outline: 'none',
  transition: 'all 0.2s ease',
},

passwordButtonGroup: {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '10px',
},

cancelButton: {
  padding: '12px 20px',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  color: '#1a2035',
  border: '1px solid #e5e7eb',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
},

saveButton: {
  padding: '12px 20px',
  borderRadius: '12px',
  backgroundColor: '#e6376e',
  color: '#ffffff',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
},

// Notifications dropdown
notificationsDropdown: {
  width: '100%',
  maxHeight: '500px',
  overflow: 'auto',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  scrollbarWidth: 'thin',
  scrollbarColor: '#e5e7eb transparent',
},

notificationsHeader: {
  padding: '16px 20px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: '#ffffff',
  zIndex: 1,
},

notificationsTitle: {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1a2035',
  margin: 0,
},

clearNotificationsButton: {
  padding: '6px 12px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  color: '#6e7a90',
  fontSize: '12px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
},

notificationsList: {
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
},

notificationItem: {
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#f9fafb',
  display: 'flex',
  gap: '16px',
  transition: 'background-color 0.2s ease',
  cursor: 'pointer',
},

notificationIcon: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  color: '#ffffff',
  flexShrink: 0,
},

notificationContent: {
  flex: 1,
},

notificationTitle: {
  fontSize: '14px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 5px 0',
},

notificationMessage: {
  fontSize: '13px',
  color: '#6e7a90',
  margin: '0 0 10px 0',
  lineHeight: '1.5',
},

notificationTime: {
  fontSize: '12px',
  color: '#9ca3af',
},

// Login page styles
loginBody: {
  minHeight: '90vh',
  backgroundColor: '#f5f7fb',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  position: 'relative',
  overflow: 'hidden',
  padding: '60px 0',
},

loginBackground: {
  position: 'absolute',
  top: 0,
  left: 0,
  
  width: '100%',
  height: '100%',
  zIndex: -1,
},

loginBackgroundOverlay: {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, , 0, 0.03)',
  zIndex: 1,
},

loginBackgroundGradient: {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(to bottom right, #e6376e10, #3b82f610)',
  zIndex: 2,
},

loginBackgroundPattern: {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  zIndex: 3,
},

loginContainer: {
  display: 'flex',
  width: '90%',
  maxWidth: '1600px',
  height: '650px',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#ffffff',
  transition: 'opacity 0.5s ease, transform 0.5s ease',
},

loginLeft: {
  flex: '0 0 50%',
  backgroundColor: '#1a2035',
  background: 'linear-gradient(90deg, #da291c 50%,rgb(255, 255, 255) 100%)',
  color: '#ffffff',
  padding: '60px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  overflow: 'hidden',
},

loginLeftContent: {
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
},

loginBrandContainer: {
  marginBottom: '40px',
},

loginLogo: {
  height: '34px',
  filter: 'brightness(0) invert(1)',
},

loginWelcomeTitle: {
  fontSize: '42px',
  fontWeight: '700',
  marginBottom: '20px',
  position: 'relative',
},

loginWelcomeText: {
  fontSize: '18px',
  lineHeight: '1.6',
  color: 'rgba(255, 255, 255, 0.8)',
  marginBottom: '40px',
  maxWidth: '400px',
},

loginFeatures: {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  marginTop: '0px',
},

loginFeatureItem: {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
},

loginFeatureIcon: {
  padding: '12px',
  borderRadius: '14px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
},

loginFeatureText: {
  fontSize: '16px',
  fontWeight: '500',
},

loginImageContainer: {
  position: 'absolute',
  bottom: '40px',
  right: '0',
  width: '420px',
  height: '240px',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  animation: 'float 6s ease-in-out infinite',
},

loginImage: {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
},

loginRight: {
  flex: '0  0 42%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  // padding: '-10px',
  // marginLeft:-120,
  backgroundColor: '#ffffff',
},

loginRightFooter: {
  marginTop: '30px',
  textAlign: 'center',
},

loginCopyright: {
  fontSize: '13px',
  color: '#6e7a90',
  margin: 0,
},

loginCard: {
  width: '80%',
  maxWidth: '450px',
  padding: '40px',
  marginTop:25,

  borderRadius: '24px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
},

loginHeader: {
  marginBottom: '30px',
  textAlign: 'center',
},

loginLogoMobile: {
  height: '30px',
  margin: '0 auto 20px',
  display: 'none',
},

loginTitle: {
  fontSize: '28px',
  fontWeight: '700',
  color: '#1a2035',
  marginBottom: '10px',
},

loginSubtitle: {
  fontSize: '16px',
  color: '#6e7a90',
  margin: 0,
},

loginForm: {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
},

formGroup: {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
},

inputLabel: {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1a2035',
},

inputWrapper: {
  position: 'relative',
  borderRadius: '14px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
},

inputIcon: {
  position: 'absolute',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#6e7a90',
},

loginInput: {
  width: '100%',
  padding: '16px 16px 16px 45px',
  border: 'none',
  fontSize: '15px',
  backgroundColor: '#f9fafb',
  outline: 'none',
},

loginButton: {
  padding: '16px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: '#da291c',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 10px rgba(255, 0, 0, 0.2)',
  marginTop: '10px',
  position: 'relative',
  overflow: 'hidden',
},

loginButtonProgress: {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  animation: 'progress 2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
},

loadingSpinnerContainer: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
},

loadingSpinner: {
  animation: 'spin 1s linear infinite',
},

loginNote: {
  fontSize: '13px',
  color: '#6e7a90',
  margin: '24px 0 0 0',
  lineHeight: '1.5',
  display: 'flex',
  alignItems: 'flex-start',
},

loginLink: {
  color: '#da291c',
  textDecoration: 'none',
  fontWeight: '500',
},

errorAlert: {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 16px',
  borderRadius: '14px',
  backgroundColor: '#fee2e2',
  color: '#ef4444',
  marginBottom: '24px',
  fontSize: '14px',
  boxShadow: '0 1px 3px rgba(185, 28, 28, 0.1)',
},

errorIcon: {
  flexShrink: 0,
},

// Welcome modal
welcomeModalOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1100,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  backdropFilter: 'blur(5px)',
},

welcomeModal: {
  width: '100%',
  maxWidth: '650px',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
},

welcomeModalHeader: {
  padding: '24px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
},

welcomeModalLogo: {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
},

welcomeLogoImage: {
  height: '28px',
},

welcomeLogoText: {
  fontSize: '20px',
  fontWeight: '600',
  color: '#1a2035',
  margin: 0,
},

welcomeModalClose: {
  padding: '8px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  color: '#1a2035',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
},

welcomeModalContent: {
  padding: '40px 30px',
},

welcomeModalTitle: {
  fontSize: '28px',
  fontWeight: '700',
  color: '#1a2035',
  margin: '0 0 16px 0',
  textAlign: 'center',
},

welcomeModalText: {
  fontSize: '16px',
  color: '#6e7a90',
  margin: '0 0 30px 0',
  lineHeight: '1.6',
  textAlign: 'center',
},

welcomeFeatures: {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
},

welcomeFeature: {
  display: 'flex',
  gap: '16px',
  padding: '20px',
  borderRadius: '16px',
  backgroundColor: '#f9fafb',
},

welcomeFeatureIcon: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  backgroundColor: 'rgba(230, 55, 110, 0.1)',
  flexShrink: 0,
},

welcomeFeatureText: {
  flex: 1,
},

welcomeFeatureTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1a2035',
  margin: '0 0 6px 0',
},

welcomeFeatureDescription: {
  fontSize: '14px',
  color: '#6e7a90',
  margin: 0,
  lineHeight: '1.5',
},

welcomeModalFooter: {
  padding: '24px',
  borderTop: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'center',
},

welcomeModalButton: {
  padding: '14px 24px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: '#e6376e',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 10px rgba(230, 55, 110, 0.2)',
},

// Page transition overlay
pageTransitionOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(5px)',
  zIndex: 900,
},
};

// Dark mode desktop styles
const darkDesktopStyles = {
dashboardContainer: {
  backgroundColor: '#121825',
},

sidebar: {
  backgroundColor: '#1a2035',
  background: 'linear-gradient(180deg, #1a2035 0%, #16192c 100%)',
  boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
},

sidebarHeader: {
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
},

collapseButton: {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
},

profileSection: {
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
},

profilePicture: {
  border: '2px solid rgba(255, 255, 255, 0.2)',
},

editProfileButton: {
  border: '2px solid #1a2035',
},

sidebarDivider: {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
},

navigation: {
  scrollbarColor: 'rgba(255, 255, 255, 0.2) rgba(0, 0, 0, 0.1)',
},

sidebarFooter: {
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
},

mainContent: {
  backgroundColor: '#121825',
},

topBar: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

pageTitle: {
  color: '#ffffff',
},

pageBreadcrumb: {
  color: '#9ca3af',
},

breadcrumbSeparator: {
  color: '#4b5563',
},

searchInput: {
  backgroundColor: '#2a3349',
  borderColor: '#2a3349',
  color: '#ffffff',
},

searchIcon: {
  color: '#9ca3af',
},

notificationButton: {
  backgroundColor: '#2a3349',
  color: '#ffffff',
},

profileDropdown: {
  backgroundColor: '#2a3349',
},

profileDropdownName: {
  color: '#ffffff',
},

contentArea: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

contentTitle: {
  color: '#ffffff',
},

contentDescription: {
  color: '#9ca3af',
},

filterSelect: {
  backgroundColor: '#2a3349',
  borderColor: '#2a3349',
  color: '#ffffff',
},

viewSwitcher: {
  borderColor: '#2a3349',
  backgroundColor: '#2a3349',
},

viewSwitcherButton: {
  color: '#9ca3af',
},

welcomeCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

welcomeCardTitle: {
  color: '#ffffff',
},

welcomeCardText: {
  color: '#9ca3af',
},

welcomeCardOutlineButton: {
  borderColor: '#2a3349',
  color: '#ffffff',
},

statCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  border: 'none',
},

statValue: {
  color: '#ffffff',
},

statTitle: {
  color: '#9ca3af',
},

recentActivityCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

recentActivityTitle: {
  color: '#ffffff',
},

recentActivityItem: {
  backgroundColor: '#2a3349',
},

recentActivityText: {
  color: '#f9fafb',
},

recentActivityUser: {
  color: '#ffffff',
},

recentActivityTarget: {
  color: '#ffffff',
},

recentActivityTime: {
  color: '#9ca3af',
},

chartCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

chartCardTitle: {
  color: '#ffffff',
},

chartCardSubtitle: {
  color: '#9ca3af',
},

chartCardSelect: {
  backgroundColor: '#2a3349',
  borderColor: '#2a3349',
  color: '#ffffff',
},

chartLabel: {
  color: '#9ca3af',
},

chartLegendItem: {
  color: '#9ca3af',
},

quickActionsCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

quickActionsTitle: {
  color: '#ffffff',
},

quickActionItem: {
  backgroundColor: '#2a3349',
},

quickActionTitle: {
  color: '#ffffff',
},

quickActionDescription: {
  color: '#9ca3af',
},

quickActionButton: {
  backgroundColor: '#1a2035',
  borderColor: '#4b5563',
  color: '#ffffff',
},

userCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  border: '1px solid #2a3349',
},

userCardBody: {
  borderTop: '1px solid #2a3349',
},

userCardName: {
  color: '#ffffff',
},

userCardEmail: {
  color: '#9ca3af',
},

tableContainer: {
  border: '1px solid #2a3349',
},

tableHead: {
  backgroundColor: '#232a45',
},

tableHeader: {
  color: '#9ca3af',
  borderBottom: '1px solid #2a3349',
},

tableRow: {
  backgroundColor: '#1a2035',
  borderBottom: '1px solid #2a3349',
},

tableCell: {
  color: '#f9fafb',
},

userName: {
  color: '#ffffff',
},

dateGroup: {
  backgroundColor: '#1a2035',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
},

historyTableHead: {
  backgroundColor: '#232a45',
},

historyTh: {
  color: '#9ca3af',
  borderBottom: '1px solid #2a3349',
},

historyRow: {
  borderBottom: '1px solid #2a3349',
},

historyTd: {
  color: '#f9fafb',
  borderBottom: '1px solid #2a3349',
},

settingsSection: {
  backgroundColor: '#2a3349',
  borderColor: '#3d4663',
},

settingsTitle: {
  color: '#ffffff',
},

settingsDescription: {
  color: '#9ca3af',
},

bulkTextarea: {
  backgroundColor: '#1a2035',
  borderColor: '#3d4663',
  color: '#ffffff',
},

resultSection: {
  backgroundColor: '#362535',
  borderColor: '#583249',
},

resultTitle: {
  color: '#f87171',
},

resultDescription: {
  color: '#9ca3af',
},

missingUser: {
  backgroundColor: '#371924',
  borderColor: '#582a37',
},

missingUserName: {
  color: '#f87171',
},

passwordModal: {
  backgroundColor: '#1a2035',
},

modalHeader: {
  backgroundColor: '#232a45',
  borderBottom: '1px solid #2a3349',
},

modalTitle: {
  color: '#ffffff',
},

modalCloseButton: {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
},

passwordLabel: {
  color: '#f9fafb',
},

passwordInput: {
  backgroundColor: '#2a3349',
  borderColor: '#3d4663',
  color: '#ffffff',
},

cancelButton: {
  backgroundColor: '#2a3349',
  borderColor: '#3d4663',
  color: '#ffffff',
},

notificationsDropdown: {
  backgroundColor: '#1a2035',
  scrollbarColor: '#3d4663 #1a2035',
},

notificationsHeader: {
  backgroundColor: '#1a2035',
  borderBottom: '1px solid #2a3349',
},

notificationsTitle: {
  color: '#ffffff',
},

clearNotificationsButton: {
  backgroundColor: '#2a3349',
  borderColor: '#3d4663',
  color: '#9ca3af',
},

notificationItem: {
  backgroundColor: '#2a3349',
},

notificationTitle: {
  color: '#ffffff',
},

notificationMessage: {
  color: '#9ca3af',
},

notificationTime: {
  color: '#6b7280',
},

// Welcome modal for dark mode
welcomeModal: {
  backgroundColor: '#1a2035',
},

welcomeModalHeader: {
  borderBottom: '1px solid #2a3349',
},

welcomeLogoText: {
  color: '#ffffff',
},

welcomeModalClose: {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
},

welcomeModalTitle: {
  color: '#ffffff',
},

welcomeModalText: {
  color: '#9ca3af',
},

welcomeFeature: {
  backgroundColor: '#2a3349',
},

welcomeFeatureTitle: {
  color: '#ffffff',
},

welcomeFeatureDescription: {
  color: '#9ca3af',
},

welcomeModalFooter: {
  borderTop: '1px solid #2a3349',
},

colorPicker: {
  backgroundColor: '#1a2035',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
},

colorPickerHeader: {
  borderBottom: '1px solid #2a3349',
},

colorPickerTitle: {
  color: '#ffffff',
},

colorPickerClose: {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
},

// Login styles for dark mode
loginBody: {
  backgroundColor: '#121825',
},

loginBackgroundOverlay: {
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
},

loginBackgroundGradient: {
  background: 'linear-gradient(to bottom right, #e6376e20, #3b82f620)',
},

loginContainer: {
  backgroundColor: '#1a2035',
},

loginLeft: {
  backgroundColor: '#151a2c',
  background: 'linear-gradient(135deg, #151a2c 0%, #1f263e 100%)',
},

loginRight: {
  backgroundColor: '#1a2035',
},

loginCard: {
  backgroundColor: '#1a2035',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
},

loginTitle: {
  color: '#ffffff',
},

loginSubtitle: {
  color: '#9ca3af',
},

inputLabel: {
  color: '#f9fafb',
},

inputWrapper: {
  borderColor: '#2a3349',
},

inputIcon: {
  color: '#9ca3af',
},

loginInput: {
  backgroundColor: '#2a3349',
  color: '#ffffff',
},

loginNote: {
  color: '#9ca3af',
},

loginCopyright: {
  color: '#9ca3af',
},

historyDatePicker: {
  backgroundColor: '#2a3349',
  borderColor: '#3d4663',
},

historyDatePickerIcon: {
  color: '#9ca3af',
},

historyDatePickerSelect: {
  color: '#ffffff',
  backgroundColor: 'transparent',
},
};

export default AdminDashboard;